import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
  Building2, Users, FileCheck, Clock, DollarSign, Plus, Upload, 
  Search, Filter, MapPin, Package, FileText, CheckCircle, AlertCircle,
  Truck, UserCheck, BarChart3, Settings, RefreshCw, Scan, FileImage,
  FileSpreadsheet, Eye, Trash2, Edit, Download, FileDown, Printer, ImagePlus,
  Mail, Send, ArrowRight, FileCheck2, CreditCard, Receipt, Calendar, Banknote,
  Bell, AlertTriangle, ClipboardList, PlusCircle, Zap, Phone, Percent, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreVertical, 
  ExternalLink, Copy, RotateCcw, ClipboardCheck, FileWarning, Link2
} from 'lucide-react';

// Vendor Management Constants
const ONBOARDING_STATUSES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  documents_pending: { label: 'Docs Pending', color: 'bg-orange-100 text-orange-800' },
  verified: { label: 'Verified', color: 'bg-blue-100 text-blue-800' },
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800' }
};

const CHECK_TYPES_LIST = [
  { id: 'id_check', name: 'ID Verification' },
  { id: 'criminal_court_check', name: 'Criminal Court Check' },
  { id: 'court_advocate_check', name: 'Court Advocate Check' },
  { id: 'address_check', name: 'Address Verification' },
  { id: 'employment_check', name: 'Employment Verification' },
  { id: 'education_check', name: 'Education Verification' },
  { id: 'reference_check', name: 'Reference Check' },
  { id: 'credit_check', name: 'Credit Check' },
  { id: 'drug_test', name: 'Drug Test' },
  { id: 'world_criminal_db', name: 'World Database Check' },
  { id: 'india_criminal_db', name: 'India Criminal Database' }
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh', 'All India'
];
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function B2BDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [cases, setCases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorMapping, setVendorMapping] = useState([]);
  const [vendorPricing, setVendorPricing] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checkTypes, setCheckTypes] = useState([]);
  const [subCheckTypes, setSubCheckTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCases, setSelectedCases] = useState([]);
  
  // Search, Filters, and Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, this_week, this_month, custom
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [sortOrder, setSortOrder] = useState('latest'); // latest, oldest
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // B2B Vendor Team State
  const [vendorTeamFilters, setVendorTeamFilters] = useState({
    clientId: 'all',
    checkType: 'all',
    status: 'all',
    tatStatus: 'all',
    dateFilterType: 'latest',
    search: ''
  });
  const [vendorTeamSelectedChecks, setVendorTeamSelectedChecks] = useState([]);
  const [showBulkInitiateDialog, setShowBulkInitiateDialog] = useState(false);
  const [bulkInitiateAutoAssign, setBulkInitiateAutoAssign] = useState(false); // Default: Manual
  const [bulkInitiateVendorId, setBulkInitiateVendorId] = useState('');
  const [bulkInitiateLoading, setBulkInitiateLoading] = useState(false);
  
  // B2B Vendor Team - Additional Action States
  const [showRaiseInsuffDialog, setShowRaiseInsuffDialog] = useState(false);
  const [showEditFieldsDialog, setShowEditFieldsDialog] = useState(false);
  const [showSendReportDialog, setShowSendReportDialog] = useState(false);
  const [showBulkReportDialog, setShowBulkReportDialog] = useState(false);
  const [selectedCheckForAction, setSelectedCheckForAction] = useState(null);
  const [insuffReason, setInsuffReason] = useState('');
  const [insuffRemarks, setInsuffRemarks] = useState('');
  const [editFieldsData, setEditFieldsData] = useState({});
  const [editFieldsRemarks, setEditFieldsRemarks] = useState('');
  const [reinitiateRemarks, setReinitiateRemarks] = useState('');
  
  // Insuff Reasons List
  const insuffReasons = [
    'Address not traceable',
    'House locked / vacant',
    'Incorrect address provided',
    'Contact number not reachable',
    'Person not available at address',
    'Document verification pending',
    'Information mismatch',
    'Incomplete documentation',
    'Neighbor confirmation required',
    'Landlord verification pending',
    'Employment details mismatch',
    'Education records not found',
    'ID proof not matching',
    'Reference not contactable',
    'Area not accessible',
    'Other'
  ];
  
  // Client Approval Workflow State
  const [pendingApprovalCases, setPendingApprovalCases] = useState([]);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [selectedCaseForApproval, setSelectedCaseForApproval] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Enhanced Vendor Management State
  const [vendorFilter, setVendorFilter] = useState('all');
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showImportVendorsDialog, setShowImportVendorsDialog] = useState(false);
  const [showVendorTatDialog, setShowVendorTatDialog] = useState(false);
  const [showVendorPenaltyDialog, setShowVendorPenaltyDialog] = useState(false);
  const [showVendorContactsDialog, setShowVendorContactsDialog] = useState(false);
  const [selectedVendorForConfig, setSelectedVendorForConfig] = useState(null);
  const [vendorFormData, setVendorFormData] = useState({
    name: '', contact_person: '', email: '', phone: '', address: '',
    city: '', state: '', gst_number: '', pan_number: '',
    specializations: [], coverage_type: 'state_specific', coverage_states: [],
    tat_days: 7, vendor_type: 'subvendor',
    bank_name: '', account_number: '', ifsc_code: '', account_holder_name: ''
  });
  
  // Dialogs
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null); // For Edit functionality
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showCaseDialog, setShowCaseDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showVendorMappingDialog, setShowVendorMappingDialog] = useState(false);
  const [showVendorPricingDialog, setShowVendorPricingDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [selectedViewCase, setSelectedViewCase] = useState(null);
  
  // Document Scanning States
  const [scanFile, setScanFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scannedRecords, setScannedRecords] = useState([]);
  const [scanSource, setScanSource] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Form states
  const [clientForm, setClientForm] = useState({
    agency_name: '', agency_code: '', contact_name: '', email: '', phone: '', phone_2: '', 
    address: '', gst_number: '', pan_number: '', billing_type: 'location_based',
    pricing: [], // Array of {check_type, state, city, price, tat_days}
    additional_contacts: [] // Array of {name, email, role} for CC in reports
  });
  const [packageForm, setPackageForm] = useState({ name: '', description: '', checks: [] });
  const [caseForm, setCaseForm] = useState({});
  const [bulkImportData, setBulkImportData] = useState('');
  const [assignForm, setAssignForm] = useState({ assigned_to: '', assigned_type: 'vendor' });
  const [mappingForm, setMappingForm] = useState({ location_name: '', check_type: 'all', vendor_id: '' });
  const [pricingForm, setPricingForm] = useState({ vendor_id: '', check_type: '', sub_check_type: 'present', location_name: '', price: 0, tat_days: 7 });
  const [invoiceForm, setInvoiceForm] = useState({ b2b_client_id: '', invoice_type: 'invoice', period_from: '', period_to: '', gst_rate: 18 });
  
  // Client pricing configuration
  const [selectedClientForPricing, setSelectedClientForPricing] = useState(null);
  const [showClientPricingDialog, setShowClientPricingDialog] = useState(false);
  const [clientPricingForm, setClientPricingForm] = useState({ check_type: '', state: 'default', city: '', price: '', tat_days: 7 });
  
  // Templates
  const [templates, setTemplates] = useState([]);
  
  // Invoice Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    company_name: 'JustVerified',
    company_address: '',
    company_gstin: '',
    company_pan: '',
    company_email: '',
    company_phone: '',
    company_logo_url: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_branch: '',
    invoice_prefix: 'JV',
    invoice_terms: 'Net 30',
    invoice_footer_text: 'This is a system-generated invoice and does not require signature.'
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showInvoiceViewDialog, setShowInvoiceViewDialog] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    cc_emails: '',
    subject: '',
    message: '',
    include_excel: true
  });
  
  // Payment Tracking
  const [showPaymentDialog, setShowPaymentDialog] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [showOutstandingReport, setShowOutstandingReport] = useState(false);
  const [outstandingData, setOutstandingData] = useState(null);
  
  // Payment Reminders
  const [showReminderDialog, setShowReminderDialog] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    recipient_email: '',
    cc_emails: '',
    message: ''
  });
  const [sendingReminder, setSendingReminder] = useState(false);
  const [clientStatement, setClientStatement] = useState(null);
  
  // Auto Reminder Settings
  const [autoReminderSettings, setAutoReminderSettings] = useState({
    enabled: false,
    reminder_intervals: [7, 14, 30],
    min_outstanding_amount: 100,
    reminder_frequency_days: 7,
    send_time_hour: 10,
    include_all_invoices: true,
    cc_internal_email: ''
  });
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);
  const [showAutoReminderPreview, setShowAutoReminderPreview] = useState(false);
  const [autoReminderPreview, setAutoReminderPreview] = useState(null);
  const [runningAutoReminders, setRunningAutoReminders] = useState(false);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({ b2b_client_id: '', status: '', check_type: '', qc_status: '' });

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch data with individual error handling for each call
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/b2b/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/b2b/clients`, { headers }),
        axios.get(`${API_URL}/api/b2b/packages`, { headers }),
        axios.get(`${API_URL}/api/b2b/cases`, { headers }),
        axios.get(`${API_URL}/api/vendors`, { headers }),
        axios.get(`${API_URL}/api/b2b/vendor-mapping`, { headers }),
        axios.get(`${API_URL}/api/b2b/vendor-pricing`, { headers }),
        axios.get(`${API_URL}/api/b2b/invoices`, { headers }),
        axios.get(`${API_URL}/api/b2b/check-types`, { headers }),
        axios.get(`${API_URL}/api/b2b/templates`, { headers }),
        axios.get(`${API_URL}/api/b2b/invoice-settings`, { headers }),
        axios.get(`${API_URL}/api/b2b/auto-reminder-settings`, { headers }),
        axios.get(`${API_URL}/api/b2b/reminder-logs`, { headers }),
        axios.get(`${API_URL}/api/b2b/auto-reminders/scheduler-status`, { headers })
      ]);
      
      // Process results - use data if successful, empty/default if failed
      const [statsRes, clientsRes, packagesRes, casesRes, vendorsRes, mappingRes, 
             pricingRes, invoicesRes, typesRes, templatesRes, settingsRes, 
             autoSettingsRes, logsRes, schedulerRes] = results;
      
      // Track failed calls for debugging
      const failedCalls = [];
      
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      else failedCalls.push('stats');
      
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data);
      else failedCalls.push('clients');
      
      if (packagesRes.status === 'fulfilled') setPackages(packagesRes.value.data);
      else failedCalls.push('packages');
      
      if (casesRes.status === 'fulfilled') setCases(casesRes.value.data);
      else failedCalls.push('cases');
      
      if (vendorsRes.status === 'fulfilled') setVendors(vendorsRes.value.data);
      else failedCalls.push('vendors');
      
      if (mappingRes.status === 'fulfilled') setVendorMapping(mappingRes.value.data);
      else failedCalls.push('vendor-mapping');
      
      if (pricingRes.status === 'fulfilled') setVendorPricing(pricingRes.value.data);
      else failedCalls.push('vendor-pricing');
      
      if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value.data);
      else failedCalls.push('invoices');
      
      if (typesRes.status === 'fulfilled') {
        setCheckTypes(typesRes.value.data.check_types || []);
        setSubCheckTypes(typesRes.value.data.sub_check_types || []);
      } else failedCalls.push('check-types');
      
      if (templatesRes.status === 'fulfilled') setTemplates(templatesRes.value.data || []);
      else failedCalls.push('templates');
      
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        setInvoiceSettings(settingsRes.value.data);
      } else if (settingsRes.status === 'rejected') {
        failedCalls.push('invoice-settings');
      }
      
      if (autoSettingsRes.status === 'fulfilled' && autoSettingsRes.value.data) {
        setAutoReminderSettings(autoSettingsRes.value.data);
      } else if (autoSettingsRes.status === 'rejected') {
        failedCalls.push('auto-reminder-settings');
      }
      
      if (logsRes.status === 'fulfilled') setReminderLogs(logsRes.value.data || []);
      else failedCalls.push('reminder-logs');
      
      if (schedulerRes.status === 'fulfilled') setSchedulerStatus(schedulerRes.value.data || null);
      else failedCalls.push('scheduler-status');
      
      // Only show error toast if critical data failed
      const criticalFailed = failedCalls.filter(c => ['stats', 'clients', 'cases'].includes(c));
      if (criticalFailed.length > 0) {
        console.error('Failed to fetch critical B2B data:', criticalFailed);
        toast.error(`Failed to load some data: ${criticalFailed.join(', ')}`);
      } else if (failedCalls.length > 0) {
        console.warn('Some non-critical B2B data failed to load:', failedCalls);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch B2B data');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // Download template
  const handleDownloadTemplate = async (templateId, fileName) => {
    try {
      toast.info(`Downloading ${fileName}...`);
      const response = await axios.get(`${API_URL}/api/b2b/templates/${templateId}/download`, {
        headers,
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${fileName}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  useEffect(() => {
    fetchData();
    fetchPendingApprovalCases();
  }, []);

  // Fetch pending approval cases
  const fetchPendingApprovalCases = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/cases/pending-approval`, { headers });
      setPendingApprovalCases(res.data || []);
    } catch (error) {
      console.log('No pending approval cases or feature not enabled');
    }
  };

  // Approve case
  const handleApproveCase = async () => {
    if (!selectedCaseForApproval) return;
    try {
      await axios.post(`${API_URL}/api/client/cases/${selectedCaseForApproval.id}/approve`, 
        { notes: approvalNotes }, 
        { headers }
      );
      toast.success('Case approved successfully');
      setShowApprovalDialog(false);
      setSelectedCaseForApproval(null);
      setApprovalNotes('');
      fetchPendingApprovalCases();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve case');
    }
  };

  // Reject case
  const handleRejectCase = async () => {
    if (!selectedCaseForApproval || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/client/cases/${selectedCaseForApproval.id}/reject`, 
        { reason: rejectionReason }, 
        { headers }
      );
      toast.success('Case rejected and sent back to employee');
      setShowRejectionDialog(false);
      setSelectedCaseForApproval(null);
      setRejectionReason('');
      fetchPendingApprovalCases();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject case');
    }
  };

  // Client CRUD
  const handleCreateClient = async () => {
    try {
      const clientData = {
        ...clientForm,
        pricing: clientForm.pricing || [],
        additional_contacts: clientForm.additional_contacts || []
      };
      await axios.post(`${API_URL}/api/b2b/clients`, clientData, { headers });
      toast.success('B2B Client created successfully');
      setShowClientDialog(false);
      setEditingClient(null);
      setClientForm({ agency_name: '', agency_code: '', contact_name: '', email: '', phone: '', address: '', gst_number: '', pan_number: '', billing_type: 'location_based', pricing: [], additional_contacts: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  // Open Edit Client Dialog
  const openEditClient = (client) => {
    setClientForm({
      agency_name: client.agency_name || '',
      agency_code: client.agency_code || '',
      contact_name: client.contact_name || '',
      email: client.email || '',
      phone: client.phone || '',
      phone_2: client.phone_2 || '',
      address: client.address || '',
      gst_number: client.gst_number || '',
      pan_number: client.pan_number || '',
      billing_type: client.billing_type || 'location_based',
      pricing: client.pricing || [],
      additional_contacts: client.additional_contacts || []
    });
    setEditingClient(client);
    setShowClientDialog(true);
  };

  // Update Client
  const handleUpdateClient = async () => {
    if (!editingClient) return;
    try {
      const clientData = {
        ...clientForm,
        pricing: clientForm.pricing || [],
        additional_contacts: clientForm.additional_contacts || []
      };
      await axios.put(`${API_URL}/api/b2b/clients/${editingClient.id}`, clientData, { headers });
      toast.success('B2B Client updated successfully');
      setShowClientDialog(false);
      setEditingClient(null);
      setClientForm({ agency_name: '', agency_code: '', contact_name: '', email: '', phone: '', address: '', gst_number: '', pan_number: '', billing_type: 'location_based', pricing: [], additional_contacts: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  // Add Additional Contact
  const handleAddAdditionalContact = () => {
    setClientForm(prev => ({
      ...prev,
      additional_contacts: [...(prev.additional_contacts || []), { name: '', email: '', role: '' }]
    }));
  };

  // Update Additional Contact
  const handleUpdateAdditionalContact = (index, field, value) => {
    setClientForm(prev => ({
      ...prev,
      additional_contacts: prev.additional_contacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  // Remove Additional Contact
  const handleRemoveAdditionalContact = (index) => {
    setClientForm(prev => ({
      ...prev,
      additional_contacts: prev.additional_contacts.filter((_, i) => i !== index)
    }));
  };

  // Client Pricing Handlers
  const handleAddClientPricing = () => {
    if (!clientPricingForm.check_type || !clientPricingForm.price) {
      toast.error('Check type and price are required');
      return;
    }

    const newPricing = {
      check_type: clientPricingForm.check_type,
      state: clientPricingForm.state || 'default',
      city: clientPricingForm.city || '',
      price: parseFloat(clientPricingForm.price),
      tat_days: parseInt(clientPricingForm.tat_days) || 7
    };

    setClientForm(prev => ({
      ...prev,
      pricing: [...(prev.pricing || []), newPricing]
    }));

    setClientPricingForm({ check_type: '', state: 'default', city: '', price: '', tat_days: 7 });
  };

  const handleRemoveClientPricing = (index) => {
    setClientForm(prev => ({
      ...prev,
      pricing: prev.pricing.filter((_, i) => i !== index)
    }));
  };

  const handleSaveClientPricing = async () => {
    if (!selectedClientForPricing) return;
    
    try {
      await axios.put(`${API_URL}/api/b2b/clients/${selectedClientForPricing.id}`, {
        pricing: selectedClientForPricing.pricing || []
      }, { headers });
      
      toast.success('Client pricing updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client pricing');
    }
  };

  const handleAddClientPricingToExisting = () => {
    if (!clientPricingForm.check_type || !clientPricingForm.price) {
      toast.error('Check type and price are required');
      return;
    }

    const newPricing = {
      check_type: clientPricingForm.check_type,
      state: clientPricingForm.state || 'default',
      city: clientPricingForm.city || '',
      price: parseFloat(clientPricingForm.price),
      tat_days: parseInt(clientPricingForm.tat_days) || 7
    };

    setSelectedClientForPricing(prev => ({
      ...prev,
      pricing: [...(prev.pricing || []), newPricing]
    }));

    setClientPricingForm({ check_type: '', state: 'default', city: '', price: '', tat_days: 7 });
  };

  const handleRemoveClientPricingFromExisting = (index) => {
    setSelectedClientForPricing(prev => ({
      ...prev,
      pricing: prev.pricing.filter((_, i) => i !== index)
    }));
  };

  // Case CRUD
  const handleCreateCase = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/cases`, caseForm, { headers });
      toast.success('B2B Case created successfully');
      setShowCaseDialog(false);
      setCaseForm({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    }
  };

  // Bulk Import
  const handleBulkImport = async () => {
    try {
      const lines = bulkImportData.trim().split('\n');
      const records = lines.map(line => {
        const parts = line.split('\t');
        // Column order matches Bulk_Import_Template.xlsx:
        // Client ID, Name, Father Name, DOB, Phone, Phone 2, Address, City, State, Pincode, 
        // Company Name, Company Address, Designation, Date of Joining, Date of Relieving,
        // University Name, College Name, Course, Year of Passing, Check Type, Verification Type,
        // Joining Company, Remarks
        return {
          client_case_id: parts[0] || '',
          name: parts[1] || '',
          father_name: parts[2] || '',
          dob: parts[3] || '',
          phone: parts[4] || '',
          phone_2: parts[5] || '',
          address: parts[6] || '',
          city: parts[7] || '',
          state: parts[8] || '',
          pincode: parts[9] || '',
          company_name: parts[10] || '',
          company_address: parts[11] || '',
          designation: parts[12] || '',
          date_of_joining: parts[13] || '',
          date_of_relieving: parts[14] || '',
          university_name: parts[15] || '',
          college_name: parts[16] || '',
          course: parts[17] || '',
          year_of_passing: parts[18] || '',
          check_type: parts[19] || 'address_pv',
          sub_check_type: parts[20] || 'present',
          joining_company_name: parts[21] || '',
          remarks: parts[22] || '',
          location_name: parts[7] || 'default' // Use city as location
        };
      });

      const response = await axios.post(`${API_URL}/api/b2b/cases/bulk-import`, {
        b2b_client_id: caseForm.b2b_client_id,
        agency_code: caseForm.agency_code,
        records
      }, { headers });
      
      toast.success(`Imported ${response.data.created_count} cases. Errors: ${response.data.error_count}`);
      setShowBulkImportDialog(false);
      setBulkImportData('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    }
  };

  // Document Scanning
  const handleScanDocument = async () => {
    if (!scanFile) {
      toast.error('Please select a file to scan');
      return;
    }
    
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', scanFile);
      formData.append('document_type', 'verification_form');
      
      const response = await axios.post(`${API_URL}/api/b2b/scan-document`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setScannedRecords(response.data.records || []);
        setScanSource(response.data.source);
        toast.success(`Extracted ${response.data.extracted_count} records from ${response.data.file_name}`);
      } else {
        toast.error(response.data.error || 'Scanning failed');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error(error.response?.data?.detail || 'Document scanning failed');
    } finally {
      setScanning(false);
    }
  };

  const handleImportScannedRecords = async () => {
    if (!caseForm.b2b_client_id || scannedRecords.length === 0) {
      toast.error('Select a client and scan documents first');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/b2b/cases/bulk-import`, {
        b2b_client_id: caseForm.b2b_client_id,
        agency_code: caseForm.agency_code,
        records: scannedRecords
      }, { headers });
      
      toast.success(response.data.message);
      setShowScanDialog(false);
      setScannedRecords([]);
      setScanFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const handleEditScannedRecord = (index, field, value) => {
    const updated = [...scannedRecords];
    updated[index] = { ...updated[index], [field]: value };
    setScannedRecords(updated);
  };

  const handleDeleteScannedRecord = (index) => {
    setScannedRecords(scannedRecords.filter((_, i) => i !== index));
  };

  // Assignment
  const handleAssignCases = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/cases/bulk-assign`, {
        case_ids: selectedCases,
        assigned_to: assignForm.assigned_to,
        assigned_type: assignForm.assigned_type
      }, { headers });
      toast.success(`Assigned ${selectedCases.length} cases`);
      setShowAssignDialog(false);
      setSelectedCases([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    }
  };

  // Auto Assign
  const handleAutoAssign = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/b2b/cases/auto-assign`, {}, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-assign failed');
    }
  };

  // ==================== ENHANCED VENDOR MANAGEMENT ====================
  
  // Reset vendor form
  const resetVendorForm = () => {
    setVendorFormData({
      name: '', contact_person: '', email: '', phone: '', address: '',
      city: '', state: '', gst_number: '', pan_number: '',
      specializations: [], coverage_type: 'state_specific', coverage_states: [],
      tat_days: 7, vendor_type: 'subvendor',
      bank_name: '', account_number: '', ifsc_code: '', account_holder_name: ''
    });
  };

  // Open edit vendor dialog
  const openEditVendor = (vendor) => {
    setVendorFormData({
      name: vendor.name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      gst_number: vendor.gst_number || '',
      pan_number: vendor.pan_number || '',
      specializations: vendor.specializations || [],
      coverage_type: vendor.coverage_type || 'state_specific',
      coverage_states: vendor.coverage_states || [],
      tat_days: vendor.tat_days || 7,
      vendor_type: vendor.vendor_type || 'subvendor',
      bank_name: vendor.bank_name || '',
      account_number: vendor.account_number || '',
      ifsc_code: vendor.ifsc_code || '',
      account_holder_name: vendor.account_holder_name || ''
    });
    setEditingVendor(vendor);
    setShowVendorDialog(true);
  };

  // Save vendor (create or update)
  const handleSaveVendor = async () => {
    try {
      if (editingVendor) {
        await axios.put(`${API_URL}/api/subvendors/${editingVendor.id}`, vendorFormData, { headers });
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${API_URL}/api/subvendors`, vendorFormData, { headers });
        toast.success('Vendor created successfully');
      }
      setShowVendorDialog(false);
      resetVendorForm();
      setEditingVendor(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  // Update vendor onboarding status
  const handleOnboardVendor = async (vendorId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/subvendors/${vendorId}/status`, { 
        onboarding_status: newStatus 
      }, { headers });
      toast.success(`Vendor status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Filter vendors by status
  const filteredVendors = vendors.filter(v => {
    if (vendorFilter === 'all') return true;
    return v.onboarding_status === vendorFilter;
  });

  // Vendor stats calculation
  const vendorStats = {
    total: vendors.length,
    active: vendors.filter(v => v.onboarding_status === 'active').length,
    pending: vendors.filter(v => v.onboarding_status === 'pending' || v.onboarding_status === 'documents_pending').length,
    verified: vendors.filter(v => v.onboarding_status === 'verified').length,
    suspended: vendors.filter(v => v.onboarding_status === 'suspended').length
  };

  // ==================== B2B VENDOR TEAM COMPUTED VALUES ====================
  
  // B2B cases are already individual checks (flat structure)
  // Transform them into the expected check format for the vendor team dashboard
  const b2bChecks = cases.map(c => ({
    check_id: c.id,
    case_id: c.id,
    internal_case_id: c.internal_case_id,
    client_case_id: c.client_case_id || c.agency_code || '',
    agency_code: c.agency_code,
    agency_name: c.agency_name,
    candidate_name: c.name,
    check_type: c.check_type,
    sub_check_type: c.sub_check_type,
    location_name: c.location_name,
    status: c.status || 'pending',
    vendor_name: c.assigned_to_name || '',
    vendor_id: c.assigned_to || '',
    initiated_at: c.assigned_at,
    vendor_assigned_at: c.assigned_at,
    completed_at: c.verified_at,
    verification_completed_at: c.verified_at,
    tat_days: c.tat_days || 7,
    created_at: c.created_at,
    case_created_at: c.created_at,
    received_date: c.received_date
  }));
  
  // Calculate TAT for each check
  const b2bChecksWithTAT = b2bChecks.map(check => {
    const tatDays = check.tat_days || 7;
    let daysElapsed = 0;
    let isWithinTat = true;
    
    const startDate = check.vendor_assigned_at || check.created_at || check.received_date;
    if (startDate) {
      const assignedDate = new Date(startDate);
      const now = new Date();
      
      if (check.completed_at || check.verification_completed_at) {
        const completedDate = new Date(check.completed_at || check.verification_completed_at);
        daysElapsed = Math.ceil((completedDate - assignedDate) / (1000 * 60 * 60 * 24));
      } else {
        daysElapsed = Math.ceil((now - assignedDate) / (1000 * 60 * 60 * 24));
      }
      
      isWithinTat = daysElapsed <= tatDays;
    }
    
    return { ...check, tat_days: tatDays, days_elapsed: daysElapsed, is_within_tat: isWithinTat };
  });
  
  // B2B Vendor Team Stats
  const b2bVendorTeamStats = {
    total: b2bChecksWithTAT.length,
    newlyAdded: b2bChecksWithTAT.filter(c => {
      if (!c.created_at && !c.case_created_at) return false;
      const addedDate = new Date(c.created_at || c.case_created_at);
      const hoursDiff = (new Date() - addedDate) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    }).length,
    notInitiated: b2bChecksWithTAT.filter(c => !c.status || c.status === 'pending' || c.status === 'not_initiated').length,
    notSent: b2bChecksWithTAT.filter(c => !c.vendor_name && (!c.status || c.status === 'pending' || c.status === 'not_initiated')).length,
    inProgress: b2bChecksWithTAT.filter(c => c.status === 'in_progress' || c.status === 'assigned').length,
    completed: b2bChecksWithTAT.filter(c => ['verified', 'completed', 'clear', 'failed', 'discrepant'].includes(c.status)).length,
    withinTat: b2bChecksWithTAT.filter(c => c.is_within_tat && !['verified', 'completed', 'clear'].includes(c.status)).length,
    overTat: b2bChecksWithTAT.filter(c => !c.is_within_tat && !['verified', 'completed', 'clear'].includes(c.status)).length,
    insufficiency: b2bChecksWithTAT.filter(c => c.status === 'insufficiency').length
  };
  
  // Filtered checks for B2B Vendor Team based on filters
  const filteredB2BChecks = b2bChecksWithTAT.filter(check => {
    const { clientId, checkType, status, tatStatus, dateFilterType, search } = vendorTeamFilters;
    
    // Client filter
    if (clientId !== 'all' && check.agency_code !== clientId) return false;
    
    // Check type filter
    if (checkType !== 'all' && check.check_type !== checkType) return false;
    
    // Status filter
    if (status !== 'all') {
      if (status === 'not_initiated' && check.status && check.status !== 'pending' && check.status !== 'not_initiated') return false;
      else if (status === 'in_progress' && check.status !== 'in_progress' && check.status !== 'assigned') return false;
      else if (status === 'completed' && !['verified', 'completed', 'clear'].includes(check.status)) return false;
      else if (status === 'insufficiency' && check.status !== 'insufficiency') return false;
    }
    
    // TAT filter
    if (tatStatus !== 'all') {
      if (tatStatus === 'within' && !check.is_within_tat) return false;
      if (tatStatus === 'over' && check.is_within_tat) return false;
    }
    
    // Date filter
    if (dateFilterType !== 'all') {
      const checkDate = new Date(check.created_at || check.case_created_at);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (dateFilterType === 'today' && checkDate < today) return false;
      if (dateFilterType === 'this_week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (checkDate < weekAgo) return false;
      }
      if (dateFilterType === 'this_month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        if (checkDate < monthAgo) return false;
      }
    }
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        (check.internal_case_id || '').toLowerCase().includes(searchLower) ||
        (check.client_case_id || '').toLowerCase().includes(searchLower) ||
        (check.candidate_name || '').toLowerCase().includes(searchLower) ||
        (check.agency_name || '').toLowerCase().includes(searchLower) ||
        (check.check_type || '').toLowerCase().includes(searchLower) ||
        (check.vendor_name || '').toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    return true;
  });
  
  // Get unique check types for filter dropdown
  const uniqueCheckTypes = [...new Set(b2bChecks.map(c => c.check_type).filter(Boolean))];

  // ==================== END B2B VENDOR TEAM ====================

  // ==================== B2B VENDOR TEAM HANDLERS ====================

  // Handle Bulk Initiate & Assign to Vendor
  const handleBulkInitiate = async () => {
    if (vendorTeamSelectedChecks.length === 0) {
      toast.error('Please select at least one check');
      return;
    }
    
    if (!bulkInitiateAutoAssign && !bulkInitiateVendorId) {
      toast.error('Please select a vendor for manual assignment');
      return;
    }
    
    setBulkInitiateLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/b2b/vendor-team/bulk-initiate`, {
        check_ids: vendorTeamSelectedChecks,
        auto_assign: bulkInitiateAutoAssign,
        vendor_id: bulkInitiateVendorId || null,
        send_email: true
      }, { headers });
      
      toast.success(`Successfully initiated ${vendorTeamSelectedChecks.length} check(s)`);
      setShowBulkInitiateDialog(false);
      setVendorTeamSelectedChecks([]);
      setBulkInitiateVendorId('');
      setBulkInitiateAutoAssign(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate checks');
    } finally {
      setBulkInitiateLoading(false);
    }
  };

  // Handle Single Check Initiate
  const handleSingleInitiate = async (check, vendorId) => {
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-team/initiate`, {
        check_id: check.check_id || check.id,
        case_id: check.case_id,
        vendor_id: vendorId,
        send_email: true
      }, { headers });
      
      toast.success('Check initiated and assigned to vendor');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate check');
    }
  };

  // Toggle check selection for vendor team
  const toggleVendorTeamCheckSelection = (checkId) => {
    setVendorTeamSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId)
        : [...prev, checkId]
    );
  };

  // Select all visible checks
  const selectAllVendorTeamChecks = () => {
    const allCheckIds = filteredB2BChecks.map(c => c.check_id || c.id);
    setVendorTeamSelectedChecks(prev => 
      prev.length === allCheckIds.length ? [] : allCheckIds
    );
  };

  // Handle Raise Insuff
  const handleRaiseInsuff = async () => {
    if (!selectedCheckForAction || !insuffReason) {
      toast.error('Please select an insufficiency reason');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-team/raise-insuff`, {
        case_id: selectedCheckForAction.case_id,
        check_id: selectedCheckForAction.check_id,
        reason: insuffReason,
        remarks: insuffRemarks
      }, { headers });
      toast.success('Insufficiency raised successfully');
      setShowRaiseInsuffDialog(false);
      setSelectedCheckForAction(null);
      setInsuffReason('');
      setInsuffRemarks('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise insufficiency');
    }
  };

  // Handle Re-initiate
  const handleReinitiate = async (check) => {
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-team/reinitiate`, {
        case_id: check.case_id,
        check_id: check.check_id,
        remarks: reinitiateRemarks || 'Re-initiated for verification'
      }, { headers });
      toast.success('Check re-initiated successfully');
      setReinitiateRemarks('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-initiate');
    }
  };

  // Handle Edit Fields
  const handleEditFields = async () => {
    if (!selectedCheckForAction) return;
    try {
      await axios.put(`${API_URL}/api/b2b/cases/${selectedCheckForAction.case_id}/fields`, {
        ...editFieldsData,
        remarks: editFieldsRemarks,
        updated_by: user?.id
      }, { headers });
      toast.success('Fields updated successfully');
      setShowEditFieldsDialog(false);
      setSelectedCheckForAction(null);
      setEditFieldsData({});
      setEditFieldsRemarks('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fields');
    }
  };

  // Handle Send Report (Single)
  const handleSendReport = async (check) => {
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-team/send-report`, {
        case_id: check.case_id,
        check_id: check.check_id,
        send_email: true
      }, { headers });
      toast.success('Report sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    }
  };

  // Handle Bulk Report
  const handleBulkReport = async () => {
    if (vendorTeamSelectedChecks.length === 0) {
      toast.error('Please select at least one check');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-team/bulk-report`, {
        check_ids: vendorTeamSelectedChecks,
        send_email: true
      }, { headers });
      toast.success(`Report sent for ${vendorTeamSelectedChecks.length} check(s)`);
      setShowBulkReportDialog(false);
      setVendorTeamSelectedChecks([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk report');
    }
  };

  // Open Edit Fields Dialog
  const openEditFieldsDialog = (check) => {
    setSelectedCheckForAction(check);
    setEditFieldsData({
      name: check.candidate_name || '',
      address: check.address || '',
      city: check.city || '',
      state: check.state || '',
      pincode: check.pincode || '',
      contact_number: check.contact_number || '',
      alternate_contact: check.alternate_contact || ''
    });
    setShowEditFieldsDialog(true);
  };

  // Open Raise Insuff Dialog
  const openRaiseInsuffDialog = (check) => {
    setSelectedCheckForAction(check);
    setShowRaiseInsuffDialog(true);
  };

  // Copy Check Details to Clipboard
  const copyCheckDetails = (check) => {
    const details = `
Case ID: ${check.internal_case_id || check.case_id}
Client Code: ${check.client_case_id || check.agency_code}
Employee: ${check.candidate_name}
Check Type: ${check.check_type}
Status: ${check.status}
Vendor: ${check.vendor_name || 'Not Assigned'}
Address: ${check.address || 'N/A'}
Contact: ${check.contact_number || 'N/A'}
    `.trim();
    navigator.clipboard.writeText(details);
    toast.success('Check details copied to clipboard');
  };

  // Copy Field Verification Link
  const copyFieldVerificationLink = (caseId) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/b2b-field/${caseId}`;
    navigator.clipboard.writeText(link);
    toast.success('Field verification link copied! Send this to the field agent.');
  };

  // Generate PDF Report for a case
  const handleGenerateReport = async (caseId) => {
    try {
      toast.info('Generating BGV Report...');
      const response = await axios.post(
        `${API_URL}/api/cases/${caseId}/generate-bgv-report`,
        { is_final: false },
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', match ? match[1] : `BGV_Report_${caseId}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  // Generate bulk reports for selected cases
  const handleBulkGenerateReports = async () => {
    if (selectedCases.length === 0) {
      toast.error('Please select at least one case');
      return;
    }
    
    try {
      toast.info(`Generating reports for ${selectedCases.length} case(s)...`);
      const response = await axios.post(
        `${API_URL}/api/b2b/cases/bulk-generate-reports`,
        { case_ids: selectedCases },
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      // Create download link for ZIP
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Verification_Reports_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedCases.length} reports downloaded`);
      setSelectedCases([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reports');
    }
  };

  // ==================== END B2B VENDOR TEAM HANDLERS ====================

  // ==================== END VENDOR MANAGEMENT ====================

  // QC Approve
  const handleBulkApprove = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/qc/bulk-approve`, {
        case_ids: selectedCases,
        remarks: 'Bulk approved'
      }, { headers });
      toast.success(`Approved ${selectedCases.length} cases`);
      setSelectedCases([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  // Vendor Mapping
  const handleSaveMapping = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-mapping`, mappingForm, { headers });
      toast.success('Vendor mapping saved');
      setShowVendorMappingDialog(false);
      setMappingForm({ location_name: '', check_type: 'all', vendor_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mapping');
    }
  };

  // Vendor Pricing
  const handleSavePricing = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/vendor-pricing`, pricingForm, { headers });
      toast.success('Pricing saved');
      setShowVendorPricingDialog(false);
      setPricingForm({ vendor_id: '', check_type: '', sub_check_type: 'present', location_name: '', price: 0, tat_days: 7 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    }
  };

  // Invoice Generation
  const handleGenerateInvoice = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/b2b/invoices/generate`, invoiceForm, { headers });
      toast.success('Invoice generated successfully');
      setShowInvoiceDialog(false);
      setInvoiceForm({ b2b_client_id: '', invoice_type: 'invoice', period_from: '', period_to: '', gst_rate: 18 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invoice generation failed');
    }
  };

  // PDF Generation for B2B Invoice
  const generateInvoicePDF = async (invoice) => {
    try {
      // Fetch complete invoice data with settings
      const response = await axios.get(`${API_URL}/api/b2b/invoices/${invoice.id}/pdf-data`, { headers });
      const { invoice: invData, client, settings } = response.data;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Header with company details
      let yPos = 15;
      
      // Add logo if available
      if (settings.company_logo_url && settings.company_logo_url.startsWith('data:')) {
        try {
          doc.addImage(settings.company_logo_url, 'PNG', 14, yPos, 40, 20);
        } catch (e) {
          console.log('Logo error:', e);
        }
      }
      
      // Company name and details (right side of logo or top if no logo)
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(settings.company_name || 'JustVerified', 60, yPos + 8);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      if (settings.company_address) {
        const addressLines = settings.company_address.split('\n');
        addressLines.forEach((line, idx) => {
          doc.text(line, 60, yPos + 14 + (idx * 4));
        });
      }
      
      // TAX INVOICE title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('TAX INVOICE', pageWidth - 14, yPos + 10, { align: 'right' });
      
      // Company IDs on right
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      if (settings.company_gstin) doc.text(`GSTIN: ${settings.company_gstin}`, pageWidth - 14, yPos + 18, { align: 'right' });
      if (settings.company_pan) doc.text(`PAN: ${settings.company_pan}`, pageWidth - 14, yPos + 23, { align: 'right' });
      
      yPos = 50;
      
      // Horizontal line
      doc.setDrawColor(200, 200, 200);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;
      
      // Invoice details section
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      
      // Left column - Invoice details
      doc.setFont('helvetica', 'bold');
      doc.text('Invoice Number:', 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(invData.invoice_number || '', 55, yPos);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Invoice Date:', 14, yPos + 6);
      doc.setFont('helvetica', 'normal');
      doc.text(invData.created_at?.split('T')[0] || '', 55, yPos + 6);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Terms:', 14, yPos + 12);
      doc.setFont('helvetica', 'normal');
      doc.text(settings.invoice_terms || 'Net 30', 55, yPos + 12);
      
      // Right column - Period
      doc.setFont('helvetica', 'bold');
      doc.text('Period From:', pageWidth - 70, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(invData.period_from || 'N/A', pageWidth - 14, yPos, { align: 'right' });
      
      doc.setFont('helvetica', 'bold');
      doc.text('Period To:', pageWidth - 70, yPos + 6);
      doc.setFont('helvetica', 'normal');
      doc.text(invData.period_to || 'N/A', pageWidth - 14, yPos + 6, { align: 'right' });
      
      yPos += 25;
      
      // Bill To section
      doc.setFillColor(245, 247, 250);
      doc.rect(14, yPos, pageWidth - 28, 30, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Bill To:', 18, yPos + 6);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(invData.agency_name || client?.agency_name || '', 18, yPos + 12);
      if (client?.address) doc.text(client.address, 18, yPos + 17);
      if (client?.gst_number) doc.text(`GSTIN: ${client.gst_number}`, 18, yPos + 22);
      if (client?.pan_number) doc.text(`PAN: ${client.pan_number}`, 18, yPos + 27);
      
      yPos += 40;
      
      // Line items table
      const tableData = (invData.items || []).map((item, idx) => [
        idx + 1,
        `${item.check_type?.replace('_pv', ' PV').toUpperCase() || 'N/A'}\n${item.name || ''}`,
        '998521', // HSN/SAC for verification services
        1,
        `₹${(item.price || 0).toFixed(2)}`,
        `${invData.gst_rate || 18}%`,
        `₹${((item.price || 0) * (invData.gst_rate || 18) / 100).toFixed(2)}`,
        `₹${(item.price || 0).toFixed(2)}`
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'IGST %', 'IGST Amt', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: { 
          fillColor: [30, 41, 59], 
          textColor: [255, 255, 255], 
          fontSize: 8,
          fontStyle: 'bold'
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 50 },
          2: { cellWidth: 20, halign: 'center' },
          3: { cellWidth: 12, halign: 'center' },
          4: { cellWidth: 22, halign: 'right' },
          5: { cellWidth: 18, halign: 'center' },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 25, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });
      
      let finalY = doc.lastAutoTable.finalY + 10;
      
      // Summary section (right aligned)
      const summaryX = pageWidth - 90;
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      
      doc.text('Sub Total:', summaryX, finalY);
      doc.text(`₹${(invData.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, pageWidth - 14, finalY, { align: 'right' });
      
      doc.text(`IGST (${invData.gst_rate || 18}%):`, summaryX, finalY + 7);
      doc.text(`₹${(invData.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, pageWidth - 14, finalY + 7, { align: 'right' });
      
      // Line before total
      doc.setDrawColor(200, 200, 200);
      doc.line(summaryX, finalY + 11, pageWidth - 14, finalY + 11);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', summaryX, finalY + 19);
      doc.text(`₹${(invData.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, pageWidth - 14, finalY + 19, { align: 'right' });
      
      finalY += 30;
      
      // Amount in words
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Amount in Words: Indian Rupee ${numberToWords(invData.total || 0)} Only`, 14, finalY);
      
      finalY += 15;
      
      // Bank Details
      if (settings.bank_name || settings.bank_account_number) {
        doc.setFillColor(245, 247, 250);
        doc.rect(14, finalY, pageWidth / 2 - 20, 30, 'F');
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Bank Details:', 18, finalY + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        if (settings.bank_name) doc.text(`Bank: ${settings.bank_name}`, 18, finalY + 12);
        if (settings.bank_account_number) doc.text(`A/C No: ${settings.bank_account_number}`, 18, finalY + 17);
        if (settings.bank_ifsc_code) doc.text(`IFSC: ${settings.bank_ifsc_code}`, 18, finalY + 22);
        if (settings.bank_branch) doc.text(`Branch: ${settings.bank_branch}`, 18, finalY + 27);
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(settings.invoice_footer_text || 'This is a system-generated invoice and does not require signature.', 14, pageHeight - 15);
      doc.text(`Generated by ${settings.company_name || 'JustVerified'}`, pageWidth - 14, pageHeight - 15, { align: 'right' });
      
      doc.save(`${invData.invoice_number || 'Invoice'}.pdf`);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Convert number to words for invoice
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
      return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
    };
    
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let result = numToWords(rupees);
    if (paise > 0) {
      result += ' and ' + numToWords(paise) + ' Paise';
    }
    return result;
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/b2b/upload-logo`, formData, {
        headers: { 
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setInvoiceSettings(prev => ({ ...prev, company_logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Save invoice settings
  const handleSaveInvoiceSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/b2b/invoice-settings`, invoiceSettings, { headers });
      toast.success('Invoice settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Download Excel for invoice/estimate
  const handleDownloadExcel = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/api/b2b/invoices/${invoice.id}/excel`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Excel');
    }
  };

  // Open email dialog
  const openEmailDialog = (invoice) => {
    const client = clients.find(c => c.id === invoice.b2b_client_id);
    setEmailForm({
      recipient_email: client?.email || '',
      cc_emails: '',
      subject: '',
      message: '',
      include_excel: true
    });
    setShowEmailDialog(invoice);
  };

  // Send email with invoice/estimate
  const handleSendEmail = async () => {
    if (!showEmailDialog) return;
    
    setSendingEmail(true);
    try {
      const emailData = {
        ...emailForm,
        cc_emails: emailForm.cc_emails.split(',').map(e => e.trim()).filter(e => e)
      };
      
      await axios.post(`${API_URL}/api/b2b/invoices/${showEmailDialog.id}/send-email`, emailData, { headers });
      toast.success(`${showEmailDialog.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} sent successfully`);
      setShowEmailDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Convert estimate to invoice
  const handleConvertToInvoice = async (estimate) => {
    try {
      const response = await axios.post(`${API_URL}/api/b2b/invoices/${estimate.id}/convert-to-invoice`, {}, { headers });
      toast.success('Estimate converted to invoice successfully');
      setShowInvoiceViewDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert estimate');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      on_hold: 'bg-gray-100 text-gray-800',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      converted: 'bg-purple-100 text-purple-800',
      paid: 'bg-emerald-100 text-emerald-800',
      partial: 'bg-amber-100 text-amber-800',
      unpaid: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (invoice) => {
    const status = invoice.payment_status || 'unpaid';
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      partial: 'bg-amber-100 text-amber-800 border-amber-300',
      unpaid: 'bg-red-100 text-red-800 border-red-300'
    };
    return (
      <Badge className={`${colors[status]} border`}>
        {status === 'paid' ? '✓ Paid' : status === 'partial' ? '◐ Partial' : '○ Unpaid'}
      </Badge>
    );
  };

  // Open payment dialog and fetch payments
  const openPaymentDialog = async (invoice) => {
    setShowPaymentDialog(invoice);
    setPaymentForm({
      amount: invoice.balance_due || invoice.total || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer',
      reference_number: '',
      notes: ''
    });
    
    // Fetch existing payments
    try {
      const response = await axios.get(`${API_URL}/api/b2b/invoices/${invoice.id}/payments`, { headers });
      setInvoicePayments(response.data.payments || []);
    } catch (error) {
      setInvoicePayments([]);
    }
  };

  // Record payment
  const handleRecordPayment = async () => {
    if (!showPaymentDialog || !paymentForm.amount) return;
    
    setRecordingPayment(true);
    try {
      await axios.post(`${API_URL}/api/b2b/invoices/${showPaymentDialog.id}/payments`, paymentForm, { headers });
      toast.success('Payment recorded successfully');
      
      // Refresh payments
      const response = await axios.get(`${API_URL}/api/b2b/invoices/${showPaymentDialog.id}/payments`, { headers });
      setInvoicePayments(response.data.payments || []);
      
      // Reset form
      setPaymentForm(prev => ({
        ...prev,
        amount: response.data.summary?.balance_due || '',
        reference_number: '',
        notes: ''
      }));
      
      // Refresh main data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  // Delete payment
  const handleDeletePayment = async (paymentId) => {
    try {
      await axios.delete(`${API_URL}/api/b2b/payments/${paymentId}`, { headers });
      toast.success('Payment deleted');
      
      // Refresh payments
      if (showPaymentDialog) {
        const response = await axios.get(`${API_URL}/api/b2b/invoices/${showPaymentDialog.id}/payments`, { headers });
        setInvoicePayments(response.data.payments || []);
      }
      
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  // Fetch outstanding report
  const fetchOutstandingReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/b2b/payments/outstanding`, { headers });
      setOutstandingData(response.data);
      setShowOutstandingReport(true);
    } catch (error) {
      toast.error('Failed to fetch outstanding report');
    }
  };

  // Open reminder dialog for a client
  const openReminderDialog = async (clientData) => {
    const client = clients.find(c => c.id === clientData.client_id);
    setReminderForm({
      recipient_email: client?.email || '',
      cc_emails: '',
      message: ''
    });
    
    // Fetch client statement
    try {
      const response = await axios.get(`${API_URL}/api/b2b/clients/${clientData.client_id}/statement`, { headers });
      setClientStatement(response.data);
      setShowReminderDialog(clientData);
    } catch (error) {
      toast.error('Failed to fetch client statement');
    }
  };

  // Send payment reminder
  const handleSendReminder = async () => {
    if (!showReminderDialog || !reminderForm.recipient_email) return;
    
    setSendingReminder(true);
    try {
      const reminderData = {
        ...reminderForm,
        cc_emails: reminderForm.cc_emails.split(',').map(e => e.trim()).filter(e => e)
      };
      
      await axios.post(`${API_URL}/api/b2b/clients/${showReminderDialog.client_id}/send-reminder`, reminderData, { headers });
      toast.success('Payment reminder sent successfully');
      setShowReminderDialog(null);
      setClientStatement(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  // Download statement Excel
  const handleDownloadStatement = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/b2b/clients/${clientId}/statement/excel`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${clientId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Statement downloaded successfully');
    } catch (error) {
      toast.error('Failed to download statement');
    }
  };

  // Get aging color
  const getAgingColor = (days) => {
    if (days > 90) return 'bg-red-600';
    if (days > 60) return 'bg-red-500';
    if (days > 30) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Save auto reminder settings
  const handleSaveAutoReminderSettings = async () => {
    setSavingAutoSettings(true);
    try {
      await axios.put(`${API_URL}/api/b2b/auto-reminder-settings`, autoReminderSettings, { headers });
      toast.success('Auto reminder settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAutoSettings(false);
    }
  };

  // Preview auto reminders
  const handlePreviewAutoReminders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/b2b/auto-reminders/preview`, { headers });
      setAutoReminderPreview(response.data);
      setShowAutoReminderPreview(true);
    } catch (error) {
      toast.error('Failed to preview reminders');
    }
  };

  // Run auto reminders
  const handleRunAutoReminders = async () => {
    setRunningAutoReminders(true);
    try {
      const response = await axios.post(`${API_URL}/api/b2b/auto-reminders/run`, {}, { headers });
      toast.success(`${response.data.reminders_sent} reminder(s) sent successfully`);
      setShowAutoReminderPreview(false);
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run reminders');
    } finally {
      setRunningAutoReminders(false);
    }
  };

  // Toggle reminder interval
  const toggleReminderInterval = (interval) => {
    setAutoReminderSettings(prev => {
      const intervals = prev.reminder_intervals || [];
      if (intervals.includes(interval)) {
        return { ...prev, reminder_intervals: intervals.filter(i => i !== interval) };
      } else {
        return { ...prev, reminder_intervals: [...intervals, interval].sort((a, b) => a - b) };
      }
    });
  };

  // Helper function to check if date is within range
  const isDateInRange = (dateStr, filterType, customRange) => {
    if (!dateStr || filterType === 'all') return true;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (filterType) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return date >= today && date <= todayEnd;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return date >= weekStart && date <= today;
      case 'this_month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return date >= monthStart && date <= today;
      case 'custom':
        if (customRange.from && customRange.to) {
          const from = new Date(customRange.from);
          const to = new Date(customRange.to);
          to.setHours(23, 59, 59, 999);
          return date >= from && date <= to;
        }
        return true;
      default:
        return true;
    }
  };

  // Filter, search, sort cases
  const filteredCases = cases
    .filter(c => {
      // Basic filters
      if (filters.b2b_client_id && c.b2b_client_id !== filters.b2b_client_id) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.check_type && c.check_type !== filters.check_type) return false;
      if (filters.qc_status && c.qc_status !== filters.qc_status) return false;
      
      // Date filter
      if (!isDateInRange(c.received_date || c.created_at, dateFilter, dateRange)) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchFields = [
          c.internal_case_id,
          c.client_case_id,
          c.name,
          c.agency_name,
          c.joining_company_name,
          c.phone,
          c.city,
          c.state
        ].filter(Boolean).map(s => s.toLowerCase());
        
        if (!searchFields.some(field => field.includes(query))) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.received_date || a.created_at);
      const dateB = new Date(b.received_date || b.created_at);
      return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });

  // Pagination
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const paginatedCases = filteredCases.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 shadow-lg">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">B2B Portal</h1>
              <p className="text-sm opacity-80">Physical Verification for Agencies</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm">Welcome, {user?.name}</span>
            <Button variant="outline" className="text-white border-white hover:bg-white/10" onClick={onLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 w-full max-w-4xl">
            <TabsTrigger value="dashboard" className="flex items-center gap-1">
              <BarChart3 className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-1">
              <Building2 className="w-4 h-4" /> Clients
            </TabsTrigger>
            <TabsTrigger value="cases" className="flex items-center gap-1">
              <FileCheck className="w-4 h-4" /> Cases
            </TabsTrigger>
            <TabsTrigger value="vendor-team" className="flex items-center gap-1">
              <Send className="w-4 h-4" /> B2B Vendor Team
            </TabsTrigger>
            <TabsTrigger value="vendors" className="flex items-center gap-1">
              <Truck className="w-4 h-4" /> Vendors
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-1">
              <FileText className="w-4 h-4" /> Invoices
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1">
              <Settings className="w-4 h-4" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4 mt-4">
            {/* Pending Approval Alert */}
            {pendingApprovalCases.length > 0 && (
              <Card className="border-amber-300 bg-amber-50">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <CardTitle className="text-amber-800">Cases Awaiting Your Approval</CardTitle>
                    </div>
                    <Badge className="bg-amber-600 text-white">{pendingApprovalCases.length} pending</Badge>
                  </div>
                  <CardDescription className="text-amber-700">
                    These employee submissions require your approval before QC can process them
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingApprovalCases.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.employee?.name || 'N/A'}</TableCell>
                          <TableCell>{c.employee?.email || 'N/A'}</TableCell>
                          <TableCell>{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(`/employees/${c.employee?.id}`, '_blank')}
                              >
                                <Eye className="w-4 h-4 mr-1" />Review
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => { setSelectedCaseForApproval(c); setShowApprovalDialog(true); }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => { setSelectedCaseForApproval(c); setShowRejectionDialog(true); }}
                              >
                                <X className="w-4 h-4 mr-1" />Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Total Clients</p>
                      <p className="text-3xl font-bold">{stats?.total_clients || 0}</p>
                    </div>
                    <Building2 className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Total Cases</p>
                      <p className="text-3xl font-bold">{stats?.total_cases || 0}</p>
                    </div>
                    <FileCheck className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Pending QC</p>
                      <p className="text-3xl font-bold">{stats?.pending_qc || 0}</p>
                    </div>
                    <Clock className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Revenue</p>
                      <p className="text-3xl font-bold">₹{(stats?.total_revenue || 0).toLocaleString()}</p>
                    </div>
                    <DollarSign className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Case Pipeline Status */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Case Pipeline Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xl font-bold text-slate-600">{stats?.draft_cases || 0}</p>
                    <p className="text-xs text-slate-500">Draft</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xl font-bold text-orange-600">{stats?.invite_sent_pending || 0}</p>
                    <p className="text-xs text-orange-600">Invite Sent</p>
                  </div>
                  <div className="text-center p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                    <p className="text-xl font-bold text-cyan-600">{stats?.details_filled_cases || 0}</p>
                    <p className="text-xs text-cyan-600">Details Filled</p>
                  </div>
                  <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <p className="text-xl font-bold text-indigo-600">{stats?.pending_client_approval || 0}</p>
                    <p className="text-xs text-indigo-600">Client Approval</p>
                  </div>
                  <div className="text-center p-3 bg-violet-50 rounded-lg border border-violet-200">
                    <p className="text-xl font-bold text-violet-600">{stats?.not_initiated_checks || 0}</p>
                    <p className="text-xs text-violet-600">Not Initiated</p>
                  </div>
                  <div className="text-center p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-xl font-bold text-teal-600">{stats?.in_progress_checks || 0}</p>
                    <p className="text-xs text-teal-600">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Case Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Case Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{stats?.pending_cases || 0}</p>
                    <p className="text-sm text-yellow-700">Pending</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{stats?.assigned_cases || 0}</p>
                    <p className="text-sm text-blue-700">Assigned</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{stats?.in_progress_cases || 0}</p>
                    <p className="text-sm text-purple-700">In Progress</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{stats?.completed_cases || 0}</p>
                    <p className="text-sm text-green-700">Completed</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{stats?.pending_invoices || 0}</p>
                    <p className="text-sm text-red-700">Pending Invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">B2B Clients (Agencies)</h2>
              <Button onClick={() => { 
                setEditingClient(null); 
                setClientForm({ agency_name: '', agency_code: '', contact_name: '', email: '', phone: '', address: '', gst_number: '', pan_number: '', billing_type: 'location_based', pricing: [], additional_contacts: [] });
                setShowClientDialog(true); 
              }} className="flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Client
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency Code</TableHead>
                      <TableHead>Agency Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Billing Type</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No B2B clients found. Add your first client to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      clients.map(client => (
                        <TableRow key={client.id}>
                          <TableCell className="font-mono font-semibold">{client.agency_code}</TableCell>
                          <TableCell>{client.agency_name}</TableCell>
                          <TableCell>{client.contact_name}</TableCell>
                          <TableCell>{client.email}</TableCell>
                          <TableCell>{client.phone}</TableCell>
                          <TableCell><Badge variant="outline">{client.billing_type}</Badge></TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openEditClient(client)}
                              data-testid={`edit-client-${client.agency_code}`}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">B2B Cases</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowScanDialog(true)} className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-none hover:from-purple-600 hover:to-indigo-600">
                  <Scan className="w-4 h-4" /> AI Scan Documents
                </Button>
                <Button variant="outline" onClick={() => setShowBulkImportDialog(true)} className="flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Paste Import
                </Button>
                <Button onClick={() => setShowCaseDialog(true)} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Add Case
                </Button>
              </div>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-4 space-y-4">
                {/* Search and Date Row */}
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by ID, name, company, phone..."
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="w-48">
                    <Label>Date Filter</Label>
                    <Select value={dateFilter} onValueChange={v => { setDateFilter(v); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="custom">Custom Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {dateFilter === 'custom' && (
                    <>
                      <div>
                        <Label>From</Label>
                        <Input
                          type="date"
                          value={dateRange.from}
                          onChange={e => setDateRange({...dateRange, from: e.target.value})}
                          className="w-40"
                        />
                      </div>
                      <div>
                        <Label>To</Label>
                        <Input
                          type="date"
                          value={dateRange.to}
                          onChange={e => setDateRange({...dateRange, to: e.target.value})}
                          className="w-40"
                        />
                      </div>
                    </>
                  )}
                  <div className="w-36">
                    <Label>Sort By</Label>
                    <Select value={sortOrder} onValueChange={v => setSortOrder(v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="latest">Latest First</SelectItem>
                        <SelectItem value="oldest">Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Client, Status, Check Type Row */}
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Client</Label>
                    <Select value={filters.b2b_client_id || 'all'} onValueChange={v => { setFilters({...filters, b2b_client_id: v === 'all' ? '' : v}); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All Clients" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.agency_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Status</Label>
                    <Select value={filters.status || 'all'} onValueChange={v => { setFilters({...filters, status: v === 'all' ? '' : v}); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Check Type</Label>
                    <Select value={filters.check_type || 'all'} onValueChange={v => { setFilters({...filters, check_type: v === 'all' ? '' : v}); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {checkTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.short}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={() => { 
                    setFilters({ b2b_client_id: '', status: '', check_type: '', qc_status: '' }); 
                    setSearchQuery(''); 
                    setDateFilter('all'); 
                    setDateRange({ from: '', to: '' });
                    setCurrentPage(1);
                  }}>
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Actions Bar */}
            {selectedCases.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{selectedCases.length} cases selected</span>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setShowAssignDialog(true)}>Assign Selected</Button>
                      <Button size="sm" variant="outline" onClick={handleBulkApprove}>QC Approve</Button>
                      <Button size="sm" variant="secondary" onClick={handleBulkGenerateReports}>
                        <FileDown className="w-3 h-3 mr-1" /> Generate Reports
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedCases([])}>Clear Selection</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-0">
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={selectedCases.length === paginatedCases.length && paginatedCases.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedCases(paginatedCases.map(c => c.id));
                              else setSelectedCases([]);
                            }}
                          />
                        </TableHead>
                        <TableHead>Internal ID</TableHead>
                        <TableHead>Client ID</TableHead>
                        <TableHead>Agency</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Joining Company</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Received Date</TableHead>
                        <TableHead>Assigned Date</TableHead>
                        <TableHead>Vendor Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Verification</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCases.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                            No cases found. Import or add cases to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedCases.map(c => (
                          <TableRow key={c.id}>
                            <TableCell>
                              <Checkbox 
                                checked={selectedCases.includes(c.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) setSelectedCases([...selectedCases, c.id]);
                                  else setSelectedCases(selectedCases.filter(id => id !== c.id));
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs font-medium">{c.internal_case_id}</TableCell>
                            <TableCell className="font-mono text-xs text-blue-600">{c.client_case_id || '-'}</TableCell>
                            <TableCell className="text-sm">{c.agency_name || c.agency_code}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{c.name}</p>
                                {c.father_name && <p className="text-xs text-gray-500">S/o {c.father_name}</p>}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{c.joining_company_name || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{c.check_type?.replace('_pv', '').replace('_', ' ').toUpperCase()}</Badge>
                              <span className="text-xs text-gray-500 ml-1">({c.sub_check_type})</span>
                            </TableCell>
                            <TableCell className="text-xs">
                              {c.received_date ? new Date(c.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : 
                               c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {c.assigned_at ? new Date(c.assigned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                            </TableCell>
                            <TableCell className="text-sm">{c.assigned_to_name || c.assigned_to || '-'}</TableCell>
                            <TableCell>{getStatusBadge(c.status)}</TableCell>
                            <TableCell>
                              {c.verification_result ? (
                                <Badge className={
                                  c.verification_result === 'green' || c.verification_result === 'clear' ? 'bg-green-100 text-green-800' :
                                  c.verification_result === 'red' || c.verification_result === 'discrepancy' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {c.verification_result.toUpperCase()}
                                </Badge>
                              ) : <span className="text-gray-400 text-xs">Pending</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" onClick={() => setSelectedViewCase(c)}>
                                  <Eye className="w-3 h-3 mr-1" />View
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleGenerateReport(c.id)} title="Generate PDF Report">
                                  <FileDown className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Pagination */}
            {filteredCases.length > 0 && (
              <Card>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <span>Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredCases.length)} of {filteredCases.length} cases</span>
                      <Select value={String(itemsPerPage)} onValueChange={v => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span>per page</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm px-2">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* B2B Vendor Team Tab - Similar to Agent Vendor Team */}
          <TabsContent value="vendor-team" className="space-y-4 mt-4">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-green-700">B2B Vendor Team</h2>
                <p className="text-slate-500 text-sm">Address, Criminal, ID & External Verifications</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowBulkReportDialog(true)}
                  disabled={vendorTeamSelectedChecks.length === 0}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Bulk Report ({vendorTeamSelectedChecks.length})
                </Button>
                <Button 
                  onClick={() => setShowBulkInitiateDialog(true)}
                  disabled={vendorTeamSelectedChecks.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Bulk Initiate ({vendorTeamSelectedChecks.length})
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-9 gap-2">
              <Card className="bg-slate-50 border-slate-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-slate-700">{b2bVendorTeamStats.total}</div>
                  <div className="text-xs text-slate-500">Total Assigned</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-blue-700">{b2bVendorTeamStats.newlyAdded}</div>
                  <div className="text-xs text-blue-600">Newly Added</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-gray-700">{b2bVendorTeamStats.notInitiated}</div>
                  <div className="text-xs text-gray-600">Not Initiated</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-orange-700">{b2bVendorTeamStats.notSent}</div>
                  <div className="text-xs text-orange-600">Not Sent</div>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-purple-700">{b2bVendorTeamStats.inProgress}</div>
                  <div className="text-xs text-purple-600">In Progress</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-green-700">{b2bVendorTeamStats.completed}</div>
                  <div className="text-xs text-green-600">Completed</div>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-emerald-700">{b2bVendorTeamStats.withinTat}</div>
                  <div className="text-xs text-emerald-600">Within TAT</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-red-700">{b2bVendorTeamStats.overTat}</div>
                  <div className="text-xs text-red-600">Over TAT</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200 cursor-pointer hover:shadow-md">
                <CardContent className="p-3 text-center">
                  <div className="text-xl font-bold text-amber-700">{b2bVendorTeamStats.insufficiency}</div>
                  <div className="text-xs text-amber-600">Insuff</div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Quick Filters:</span>
              <div className="flex gap-1">
                {[
                  { id: 'latest', label: 'Latest', icon: RefreshCw },
                  { id: 'today', label: 'Today', icon: Calendar },
                  { id: 'this_week', label: 'This Week', icon: Calendar },
                  { id: 'this_month', label: 'This Month', icon: Calendar }
                ].map(f => (
                  <Button
                    key={f.id}
                    variant={vendorTeamFilters.dateFilterType === f.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVendorTeamFilters(prev => ({ ...prev, dateFilterType: f.id }))}
                    className={vendorTeamFilters.dateFilterType === f.id ? 'bg-green-600' : ''}
                  >
                    <f.icon className="w-3 h-3 mr-1" />
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap items-center gap-3">
              <Select 
                value={vendorTeamFilters.clientId} 
                onValueChange={v => setVendorTeamFilters(prev => ({ ...prev, clientId: v }))}
              >
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.agency_code}>{c.agency_code} - {c.agency_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={vendorTeamFilters.checkType} 
                onValueChange={v => setVendorTeamFilters(prev => ({ ...prev, checkType: v }))}
              >
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {uniqueCheckTypes.map(ct => (
                    <SelectItem key={ct} value={ct}>{ct.replace('_pv', '').replace('_', ' ').toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={vendorTeamFilters.status} 
                onValueChange={v => setVendorTeamFilters(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="not_initiated">Not Initiated</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="insufficiency">Insufficiency</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={vendorTeamFilters.tatStatus} 
                onValueChange={v => setVendorTeamFilters(prev => ({ ...prev, tatStatus: v }))}
              >
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="All TAT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All TAT</SelectItem>
                  <SelectItem value="within">Within TAT</SelectItem>
                  <SelectItem value="over">Over TAT</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={vendorTeamFilters.search}
                  onChange={e => setVendorTeamFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-9 h-9"
                />
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setVendorTeamFilters({ clientId: 'all', checkType: 'all', status: 'all', tatStatus: 'all', dateFilterType: 'latest', search: '' })}
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>

            {/* Checks Table */}
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-slate-100 z-10">
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={vendorTeamSelectedChecks.length > 0 && vendorTeamSelectedChecks.length === filteredB2BChecks.length}
                            onCheckedChange={selectAllVendorTeamChecks}
                          />
                        </TableHead>
                        <TableHead>Case ID</TableHead>
                        <TableHead>Client Code</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Initiated</TableHead>
                        <TableHead>Assigned/Sent</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>TAT</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredB2BChecks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                            No checks found matching the filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredB2BChecks.map((check, idx) => (
                          <TableRow key={check.check_id || check.id || idx} className={!check.is_within_tat ? 'bg-red-50' : ''}>
                            <TableCell>
                              <Checkbox 
                                checked={vendorTeamSelectedChecks.includes(check.check_id || check.id)}
                                onCheckedChange={() => toggleVendorTeamCheckSelection(check.check_id || check.id)}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs">{check.internal_case_id || check.case_id}</TableCell>
                            <TableCell className="font-semibold text-indigo-600">{check.client_case_id || check.agency_code}</TableCell>
                            <TableCell className="text-sm">{check.candidate_name}</TableCell>
                            <TableCell className="text-xs">{check.agency_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {(check.check_type || '').replace('_pv', '').replace('_', ' ').toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(check.status)}</TableCell>
                            <TableCell className="text-sm">{check.vendor_name || <span className="text-gray-400">Not Assigned</span>}</TableCell>
                            <TableCell className="text-xs">
                              {check.initiated_at ? new Date(check.initiated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {check.vendor_assigned_at || check.assigned_date ? 
                                new Date(check.vendor_assigned_at || check.assigned_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {check.completed_at || check.verification_completed_at ? 
                                new Date(check.completed_at || check.verification_completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className={`text-xs font-medium ${check.is_within_tat ? 'text-green-600' : 'text-red-600'}`}>
                                  {check.days_elapsed}/{check.tat_days}
                                </span>
                                {!check.is_within_tat && <AlertTriangle className="w-3 h-3 text-red-500" />}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {/* Initiate Button for unassigned */}
                                {!check.vendor_name && (
                                  <Select onValueChange={(vendorId) => handleSingleInitiate(check, vendorId)}>
                                    <SelectTrigger className="h-7 w-20 text-xs bg-green-50 border-green-200 text-green-700">
                                      <Send className="w-3 h-3 mr-1" />
                                      <span>Initiate</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <div className="px-2 py-1.5 text-xs font-medium text-gray-500">Select Vendor</div>
                                      {vendors.filter(v => v.onboarding_status === 'active').map(v => (
                                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                                
                                {/* Actions Dropdown */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={() => setSelectedViewCase(cases.find(c => c.id === check.case_id))}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => window.open(`/field-form/${check.case_id}`, '_blank')}>
                                      <ExternalLink className="w-4 h-4 mr-2" />
                                      Field Form
                                    </DropdownMenuItem>
                                    {['verified', 'completed', 'clear'].includes(check.status) && (
                                      <DropdownMenuItem onClick={() => window.open(`/view-form/${check.case_id}`, '_blank')}>
                                        <ClipboardCheck className="w-4 h-4 mr-2" />
                                        View Form
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => copyCheckDetails(check)}>
                                      <Copy className="w-4 h-4 mr-2" />
                                      Copy Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => copyFieldVerificationLink(check.case_id)}>
                                      <Link2 className="w-4 h-4 mr-2" />
                                      Copy Field Link
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleGenerateReport(check.case_id)}>
                                      <FileDown className="w-4 h-4 mr-2" />
                                      Generate PDF Report
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuSeparator />
                                    
                                    <DropdownMenuItem onClick={() => openEditFieldsDialog(check)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Edit Fields
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => openRaiseInsuffDialog(check)}
                                      className="text-orange-600"
                                    >
                                      <FileWarning className="w-4 h-4 mr-2" />
                                      Raise Insuff
                                    </DropdownMenuItem>
                                    
                                    {check.status === 'insufficiency' && (
                                      <DropdownMenuItem 
                                        onClick={() => handleReinitiate(check)}
                                        className="text-blue-600"
                                      >
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        Re-Initiate
                                      </DropdownMenuItem>
                                    )}
                                    
                                    <DropdownMenuSeparator />
                                    
                                    <DropdownMenuItem 
                                      onClick={() => handleSendReport(check)}
                                      className="text-green-600"
                                    >
                                      <Mail className="w-4 h-4 mr-2" />
                                      Send Report
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Results Count */}
            <div className="text-sm text-slate-500">
              Showing {filteredB2BChecks.length} of {b2bChecksWithTAT.length} checks
              {vendorTeamSelectedChecks.length > 0 && (
                <span className="ml-2 text-green-600 font-medium">({vendorTeamSelectedChecks.length} selected)</span>
              )}
            </div>
          </TabsContent>

          {/* Vendors Tab - Enhanced Version */}
          <TabsContent value="vendors" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-indigo-700">Vendor Management</h2>
                <p className="text-slate-500 text-sm">Onboard and manage verification vendors</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowImportVendorsDialog(true)}
                  variant="outline"
                  className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Vendors
                </Button>
                <Button 
                  onClick={() => { resetVendorForm(); setEditingVendor(null); setShowVendorDialog(true); }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add New Vendor
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-5 gap-4">
              <Card className="bg-slate-50 border-slate-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('all')}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-slate-700">{vendorStats.total}</div>
                  <div className="text-xs text-slate-500">Total Vendors</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('active')}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-700">{vendorStats.active}</div>
                  <div className="text-xs text-green-600">Active</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('pending')}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-yellow-700">{vendorStats.pending}</div>
                  <div className="text-xs text-yellow-600">Pending</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('verified')}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-700">{vendorStats.verified}</div>
                  <div className="text-xs text-blue-600">Verified</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('suspended')}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-red-700">{vendorStats.suspended}</div>
                  <div className="text-xs text-red-600">Suspended</div>
                </CardContent>
              </Card>
            </div>

            {/* Filter Badge */}
            {vendorFilter !== 'all' && (
              <div className="flex items-center gap-2">
                <Badge className={ONBOARDING_STATUSES[vendorFilter]?.color || 'bg-gray-100'}>
                  Showing: {ONBOARDING_STATUSES[vendorFilter]?.label || vendorFilter}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => setVendorFilter('all')}>
                  <X className="w-4 h-4" /> Clear
                </Button>
              </div>
            )}

            {/* Vendors Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-indigo-50">
                      <TableHead>Vendor Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Specializations</TableHead>
                      <TableHead>TAT</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Performance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVendors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p>No vendors found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredVendors.map((vendor) => (
                        <TableRow key={vendor.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div>
                              <p className="font-semibold">{vendor.name}</p>
                              <p className="text-xs text-slate-500">{vendor.contact_person}</p>
                              {vendor.gst_number && (
                                <p className="text-xs text-blue-600">GST: {vendor.gst_number}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{vendor.email}</p>
                              <p className="text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{vendor.phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {vendor.coverage_type === 'pan_india' ? 'Pan India' : 
                               vendor.coverage_states?.length ? `${vendor.coverage_states.length} States` : 'Not Set'}
                            </Badge>
                            {vendor.coverage_states?.length > 0 && vendor.coverage_type !== 'pan_india' && (
                              <p className="text-xs text-slate-500 mt-1 truncate max-w-[150px]" title={vendor.coverage_states?.join(', ')}>
                                {vendor.coverage_states?.slice(0, 3).join(', ')}{vendor.coverage_states?.length > 3 ? '...' : ''}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {vendor.specializations?.slice(0, 2).map(spec => (
                                <Badge key={spec} variant="outline" className="text-xs bg-slate-50">
                                  {spec.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                              {vendor.specializations?.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{vendor.specializations.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-800">{vendor.tat_days || 7}d</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={ONBOARDING_STATUSES[vendor.onboarding_status]?.color || 'bg-gray-100'}>
                              {ONBOARDING_STATUSES[vendor.onboarding_status]?.label || vendor.onboarding_status || 'Pending'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs">
                              <p>Assigned: <span className="font-semibold">{vendor.total_assigned || 0}</span></p>
                              <p>Completed: <span className="font-semibold text-green-600">{vendor.total_completed || 0}</span></p>
                              <p>Avg TAT: <span className="font-semibold">{vendor.avg_tat_days?.toFixed(1) || '-'}d</span></p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end flex-wrap">
                              <Button variant="outline" size="sm" onClick={() => openEditVendor(vendor)} title="Edit">
                                <Settings className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  setPricingForm({ ...pricingForm, vendor_id: vendor.id });
                                  setShowVendorPricingDialog(true);
                                }} 
                                title="Configure Pricing"
                                className="text-green-600"
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Configure TAT"
                                className="text-orange-600"
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                              {vendor.onboarding_status === 'pending' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-blue-600"
                                  onClick={() => handleOnboardVendor(vendor.id, 'verified')}
                                  title="Verify"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {vendor.onboarding_status === 'verified' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-green-600"
                                  onClick={() => handleOnboardVendor(vendor.id, 'active')}
                                  title="Activate"
                                >
                                  <Zap className="w-4 h-4" />
                                </Button>
                              )}
                              {vendor.onboarding_status === 'active' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-red-600"
                                  onClick={() => handleOnboardVendor(vendor.id, 'suspended')}
                                  title="Suspend"
                                >
                                  <AlertTriangle className="w-4 h-4" />
                                </Button>
                              )}
                              {vendor.onboarding_status === 'suspended' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-green-600"
                                  onClick={() => handleOnboardVendor(vendor.id, 'active')}
                                  title="Reactivate"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Location Mapping Section */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Location → Vendor Mapping</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowVendorMappingDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Mapping
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Location</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorMapping.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-4 text-gray-500">
                            No mappings configured
                          </TableCell>
                        </TableRow>
                      ) : (
                        vendorMapping.map(m => (
                          <TableRow key={m.id}>
                            <TableCell>{m.location_name}</TableCell>
                            <TableCell><Badge variant="outline">{m.check_type}</Badge></TableCell>
                            <TableCell>{m.vendor_name}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Vendor Pricing</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowVendorPricingDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Pricing
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorPricing.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-4 text-gray-500">
                            No pricing configured
                          </TableCell>
                        </TableRow>
                      ) : (
                        vendorPricing.slice(0, 5).map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{p.vendor_name || vendors.find(v => v.id === p.vendor_id)?.name}</TableCell>
                            <TableCell><Badge variant="outline">{p.check_type}</Badge></TableCell>
                            <TableCell>{p.location_name || 'Default'}</TableCell>
                            <TableCell className="font-semibold">₹{p.price}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-gradient-to-r from-indigo-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-indigo-800">Quick Actions</h3>
                    <p className="text-sm text-indigo-600">Manage vendor assignments efficiently</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAutoAssign} className="bg-indigo-600 hover:bg-indigo-700">
                      <RefreshCw className="w-4 h-4 mr-2" /> Auto Assign Pending Cases
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add/Edit Vendor Dialog */}
          <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vendor Name *</Label>
                      <Input
                        value={vendorFormData.name}
                        onChange={(e) => setVendorFormData({...vendorFormData, name: e.target.value})}
                        placeholder="Company Name"
                      />
                    </div>
                    <div>
                      <Label>Contact Person *</Label>
                      <Input
                        value={vendorFormData.contact_person}
                        onChange={(e) => setVendorFormData({...vendorFormData, contact_person: e.target.value})}
                        placeholder="Point of Contact"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={vendorFormData.email}
                        onChange={(e) => setVendorFormData({...vendorFormData, email: e.target.value})}
                        placeholder="contact@vendor.com"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        value={vendorFormData.phone}
                        onChange={(e) => setVendorFormData({...vendorFormData, phone: e.target.value})}
                        placeholder="9876543210"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={vendorFormData.address}
                        onChange={(e) => setVendorFormData({...vendorFormData, address: e.target.value})}
                        placeholder="Office Address"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Business Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Vendor Type</Label>
                      <Select
                        value={vendorFormData.vendor_type}
                        onValueChange={(v) => setVendorFormData({...vendorFormData, vendor_type: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subvendor">Subvendor</SelectItem>
                          <SelectItem value="agency_partner">Agency Partner</SelectItem>
                          <SelectItem value="field_agent_vendor">Field Agent Vendor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>GST Number</Label>
                      <Input
                        value={vendorFormData.gst_number}
                        onChange={(e) => setVendorFormData({...vendorFormData, gst_number: e.target.value.toUpperCase()})}
                        placeholder="29ABCDE1234F1Z5"
                      />
                    </div>
                    <div>
                      <Label>PAN Number</Label>
                      <Input
                        value={vendorFormData.pan_number}
                        onChange={(e) => setVendorFormData({...vendorFormData, pan_number: e.target.value.toUpperCase()})}
                        placeholder="ABCDE1234F"
                      />
                    </div>
                    <div>
                      <Label>Default TAT (Days)</Label>
                      <Input
                        type="number"
                        value={vendorFormData.tat_days}
                        onChange={(e) => setVendorFormData({...vendorFormData, tat_days: parseInt(e.target.value) || 7})}
                      />
                    </div>
                  </div>
                </div>

                {/* Coverage & Specializations */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Coverage & Specializations</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Coverage Type</Label>
                      <Select
                        value={vendorFormData.coverage_type}
                        onValueChange={(v) => setVendorFormData({...vendorFormData, coverage_type: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pan_india">Pan India</SelectItem>
                          <SelectItem value="regional">Regional (Multiple States)</SelectItem>
                          <SelectItem value="city_specific">City Specific</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {vendorFormData.coverage_type !== 'pan_india' && (
                      <div>
                        <Label>Coverage States</Label>
                        <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white">
                          {INDIAN_STATES.map(state => (
                            <div key={state} className="flex items-center gap-2 py-1">
                              <Checkbox
                                id={`b2b-state-${state}`}
                                checked={vendorFormData.coverage_states.includes(state)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setVendorFormData({...vendorFormData, coverage_states: [...vendorFormData.coverage_states, state]});
                                  } else {
                                    setVendorFormData({...vendorFormData, coverage_states: vendorFormData.coverage_states.filter(s => s !== state)});
                                  }
                                }}
                              />
                              <label htmlFor={`b2b-state-${state}`} className="text-sm">{state}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Check Types Handled</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {CHECK_TYPES_LIST.map(check => (
                        <Badge
                          key={check.id}
                          className={`cursor-pointer ${vendorFormData.specializations.includes(check.id) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                          onClick={() => {
                            if (vendorFormData.specializations.includes(check.id)) {
                              setVendorFormData({...vendorFormData, specializations: vendorFormData.specializations.filter(s => s !== check.id)});
                            } else {
                              setVendorFormData({...vendorFormData, specializations: [...vendorFormData.specializations, check.id]});
                            }
                          }}
                        >
                          {check.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Bank Details (for Payments)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={vendorFormData.bank_name}
                        onChange={(e) => setVendorFormData({...vendorFormData, bank_name: e.target.value})}
                        placeholder="Bank Name"
                      />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input
                        value={vendorFormData.account_number}
                        onChange={(e) => setVendorFormData({...vendorFormData, account_number: e.target.value})}
                        placeholder="Account Number"
                      />
                    </div>
                    <div>
                      <Label>IFSC Code</Label>
                      <Input
                        value={vendorFormData.ifsc_code}
                        onChange={(e) => setVendorFormData({...vendorFormData, ifsc_code: e.target.value.toUpperCase()})}
                        placeholder="IFSC Code"
                      />
                    </div>
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input
                        value={vendorFormData.account_holder_name}
                        onChange={(e) => setVendorFormData({...vendorFormData, account_holder_name: e.target.value})}
                        placeholder="Account Holder Name"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setShowVendorDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveVendor} className="bg-indigo-600 hover:bg-indigo-700">
                  {editingVendor ? 'Update Vendor' : 'Create Vendor'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Invoices & Estimates</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={fetchOutstandingReport}
                  className="text-red-600 hover:text-red-700"
                >
                  <Receipt className="w-4 h-4 mr-2" /> Outstanding Report
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setInvoiceForm(prev => ({ ...prev, invoice_type: 'estimate' }));
                    setShowInvoiceDialog(true);
                  }}
                >
                  <FileCheck2 className="w-4 h-4 mr-2" /> Generate Estimate
                </Button>
                <Button onClick={() => {
                  setInvoiceForm(prev => ({ ...prev, invoice_type: 'invoice' }));
                  setShowInvoiceDialog(true);
                }}>
                  <FileText className="w-4 h-4 mr-2" /> Generate Invoice
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Estimates</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {invoices.filter(i => i.invoice_type === 'estimate').length}
                      </p>
                    </div>
                    <FileCheck2 className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Invoices</p>
                      <p className="text-2xl font-bold text-green-800">
                        {invoices.filter(i => i.invoice_type === 'invoice').length}
                      </p>
                    </div>
                    <FileText className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600">Paid</p>
                      <p className="text-2xl font-bold text-emerald-800">
                        ₹{invoices.filter(i => i.payment_status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600">Outstanding</p>
                      <p className="text-2xl font-bold text-red-800">
                        ₹{invoices.filter(i => i.invoice_type === 'invoice' && i.payment_status !== 'paid').reduce((sum, i) => sum + (i.balance_due || i.total || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-600">Total Value</p>
                      <p className="text-2xl font-bold text-amber-800">
                        ₹{invoices.reduce((sum, i) => sum + (i.total || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <DollarSign className="w-8 h-8 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document #</TableHead>
                      <TableHead>Agency</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          No invoices or estimates generated yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map(inv => (
                        <TableRow key={inv.id} className={inv.invoice_type === 'estimate' ? 'bg-blue-50/50' : inv.payment_status === 'paid' ? 'bg-emerald-50/30' : ''}>
                          <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                          <TableCell>{inv.agency_name}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={inv.invoice_type === 'estimate' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-green-300 text-green-700 bg-green-50'}
                            >
                              {inv.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">₹{inv.total?.toLocaleString()}</TableCell>
                          <TableCell>
                            {inv.invoice_type === 'invoice' ? (
                              getPaymentStatusBadge(inv)
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {inv.invoice_type === 'invoice' ? (
                              <span className={inv.balance_due > 0 ? 'text-red-600 font-semibold' : 'text-emerald-600'}>
                                ₹{(inv.balance_due ?? inv.total)?.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(inv.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowInvoiceViewDialog(inv)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => generateInvoicePDF(inv)}
                                className="text-green-600 hover:text-green-700"
                                title="Download PDF"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDownloadExcel(inv)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Download Excel"
                              >
                                <FileSpreadsheet className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEmailDialog(inv)}
                                className="text-purple-600 hover:text-purple-700"
                                title="Send via Email"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              {inv.invoice_type === 'invoice' && inv.payment_status !== 'paid' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openPaymentDialog(inv)}
                                  className="text-emerald-600 hover:text-emerald-700"
                                  title="Record Payment"
                                >
                                  <CreditCard className="w-4 h-4" />
                                </Button>
                              )}
                              {inv.invoice_type === 'estimate' && inv.status !== 'converted' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleConvertToInvoice(inv)}
                                  className="text-amber-600 hover:text-amber-700"
                                  title="Convert to Invoice"
                                >
                                  <ArrowRight className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4 mt-4">
            {/* Invoice Settings Section */}
            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Invoice Settings
                </CardTitle>
                <CardDescription>
                  Customize your invoice appearance, company details, and bank information for PDF export.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Company Logo</Label>
                  <div className="flex items-center gap-4">
                    {invoiceSettings.company_logo_url ? (
                      <div className="relative">
                        <img 
                          src={invoiceSettings.company_logo_url} 
                          alt="Company Logo" 
                          className="h-20 w-auto object-contain border rounded p-2 bg-white"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-500 text-white rounded-full hover:bg-red-600"
                          onClick={() => setInvoiceSettings(prev => ({ ...prev, company_logo_url: '' }))}
                        >
                          ×
                        </Button>
                      </div>
                    ) : (
                      <div className="h-20 w-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-white">
                        <span className="text-gray-400 text-sm">No logo</span>
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                      >
                        <ImagePlus className="w-4 h-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">Max size: 2MB. Formats: JPEG, PNG, GIF, WEBP</p>
                    </div>
                  </div>
                </div>

                {/* Company Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={invoiceSettings.company_name}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="JustVerified"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_gstin">GSTIN</Label>
                    <Input
                      id="company_gstin"
                      value={invoiceSettings.company_gstin}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_gstin: e.target.value }))}
                      placeholder="29ABCDE1234F1ZH"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_pan">PAN</Label>
                    <Input
                      id="company_pan"
                      value={invoiceSettings.company_pan}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_pan: e.target.value }))}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_email">Email</Label>
                    <Input
                      id="company_email"
                      type="email"
                      value={invoiceSettings.company_email}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_email: e.target.value }))}
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_phone">Phone</Label>
                    <Input
                      id="company_phone"
                      value={invoiceSettings.company_phone}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
                    <Input
                      id="invoice_prefix"
                      value={invoiceSettings.invoice_prefix}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                      placeholder="JV"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_address">Company Address</Label>
                  <Textarea
                    id="company_address"
                    value={invoiceSettings.company_address}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, company_address: e.target.value }))}
                    placeholder="Enter complete company address..."
                    rows={3}
                  />
                </div>

                {/* Bank Details */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Bank Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bank_name">Bank Name</Label>
                      <Input
                        id="bank_name"
                        value={invoiceSettings.bank_name}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, bank_name: e.target.value }))}
                        placeholder="HDFC Bank"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bank_account_number">Account Number</Label>
                      <Input
                        id="bank_account_number"
                        value={invoiceSettings.bank_account_number}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, bank_account_number: e.target.value }))}
                        placeholder="50200012345678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bank_ifsc_code">IFSC Code</Label>
                      <Input
                        id="bank_ifsc_code"
                        value={invoiceSettings.bank_ifsc_code}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, bank_ifsc_code: e.target.value }))}
                        placeholder="HDFC0000123"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bank_branch">Branch</Label>
                      <Input
                        id="bank_branch"
                        value={invoiceSettings.bank_branch}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, bank_branch: e.target.value }))}
                        placeholder="Koramangala Branch"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Terms */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-3">Invoice Terms</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_terms">Payment Terms</Label>
                      <Input
                        id="invoice_terms"
                        value={invoiceSettings.invoice_terms}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_terms: e.target.value }))}
                        placeholder="Net 30"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="invoice_footer_text">Footer Text</Label>
                      <Textarea
                        id="invoice_footer_text"
                        value={invoiceSettings.invoice_footer_text}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_footer_text: e.target.value }))}
                        placeholder="This is a system-generated invoice..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveInvoiceSettings}
                  disabled={savingSettings}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  {savingSettings ? 'Saving...' : 'Save Invoice Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Auto Payment Reminders Section */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-600" />
                  Auto Payment Reminders
                </CardTitle>
                <CardDescription>
                  Configure automated payment reminders for overdue invoices. Reminders are sent based on aging intervals.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                  <div>
                    <p className="font-semibold">Enable Auto Reminders</p>
                    <p className="text-sm text-gray-500">Automatically send payment reminders to clients with overdue invoices</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${autoReminderSettings.enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {autoReminderSettings.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Checkbox 
                      checked={autoReminderSettings.enabled}
                      onCheckedChange={(checked) => setAutoReminderSettings(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>
                </div>

                {/* Reminder Intervals */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Reminder Intervals (Days Overdue)</Label>
                  <div className="flex flex-wrap gap-2">
                    {[7, 14, 21, 30, 45, 60, 90].map(interval => (
                      <Badge 
                        key={interval}
                        variant="outline"
                        className={`cursor-pointer px-4 py-2 ${
                          autoReminderSettings.reminder_intervals?.includes(interval) 
                            ? 'bg-amber-100 border-amber-400 text-amber-800' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => toggleReminderInterval(interval)}
                      >
                        {interval} days
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Selected: {autoReminderSettings.reminder_intervals?.join(', ') || 'None'} days
                  </p>
                </div>

                {/* Other Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min_amount">Minimum Outstanding Amount (₹)</Label>
                    <Input
                      id="min_amount"
                      type="number"
                      value={autoReminderSettings.min_outstanding_amount}
                      onChange={(e) => setAutoReminderSettings(prev => ({ ...prev, min_outstanding_amount: parseFloat(e.target.value) || 0 }))}
                      placeholder="100"
                    />
                    <p className="text-xs text-gray-500">Skip clients with balance below this amount</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="frequency_days">Reminder Frequency (Days)</Label>
                    <Input
                      id="frequency_days"
                      type="number"
                      value={autoReminderSettings.reminder_frequency_days}
                      onChange={(e) => setAutoReminderSettings(prev => ({ ...prev, reminder_frequency_days: parseInt(e.target.value) || 7 }))}
                      placeholder="7"
                    />
                    <p className="text-xs text-gray-500">Don't send more than once per X days</p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="cc_internal">CC Internal Email</Label>
                    <Input
                      id="cc_internal"
                      type="email"
                      value={autoReminderSettings.cc_internal_email}
                      onChange={(e) => setAutoReminderSettings(prev => ({ ...prev, cc_internal_email: e.target.value }))}
                      placeholder="accounts@yourcompany.com"
                    />
                    <p className="text-xs text-gray-500">Get copied on all auto reminders (optional)</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  <Button 
                    onClick={handleSaveAutoReminderSettings}
                    disabled={savingAutoSettings}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    {savingAutoSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handlePreviewAutoReminders}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Who Will Receive
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleRunAutoReminders}
                    disabled={runningAutoReminders || !autoReminderSettings.enabled}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    {runningAutoReminders ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Run Now
                      </>
                    )}
                  </Button>
                </div>

                {/* Reminder History */}
                {reminderLogs.length > 0 && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Recent Reminder History
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {reminderLogs.slice(0, 10).map((log) => (
                        <div key={log.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                          <div className="flex items-center gap-2">
                            {log.is_auto && <Badge className="bg-purple-100 text-purple-700 text-xs">Auto</Badge>}
                            <span className="font-medium">{log.agency_name}</span>
                            <span className="text-gray-500">({log.invoices_count} invoice{log.invoices_count > 1 ? 's' : ''})</span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-500">
                            <span>₹{log.total_outstanding?.toLocaleString()}</span>
                            <span>{log.sent_at?.split('T')[0]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Document Templates Section */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileDown className="w-5 h-5 text-purple-600" />
                  Document Templates
                </CardTitle>
                <CardDescription>
                  Download Excel templates optimized for AI scanner. Fill data in these templates for accurate extraction.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(template => (
                    <Card key={template.id} className="bg-white hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{template.name}</h4>
                            <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                            <div className="mt-2">
                              <Badge variant="outline" className="text-xs">
                                {template.check_type === 'mixed' ? 'All Types' : template.check_type.replace('_pv', ' PV').toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          className="w-full mt-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                          onClick={() => handleDownloadTemplate(template.id, template.file_name)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {/* Quick Tips */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Tips for Best AI Extraction
                  </h4>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    <li>• Keep column headers exactly as in the template</li>
                    <li>• Use DD/MM/YYYY format for all dates</li>
                    <li>• Fill city and state for accurate location pricing</li>
                    <li>• Use "Check Type" column for mixed imports (address_pv, education_pv, employment_pv)</li>
                    <li>• Don't merge cells or add extra formatting</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Check Types Section */}
            <Card>
              <CardHeader>
                <CardTitle>B2B Settings</CardTitle>
                <CardDescription>Configure packages, check types, and pricing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Check Types</h3>
                  <div className="flex flex-wrap gap-2">
                    {checkTypes.map(t => (
                      <Badge key={t.id} variant="outline" className="py-1 px-3">
                        {t.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Sub Check Types</h3>
                  <div className="flex flex-wrap gap-2">
                    {subCheckTypes.map(t => (
                      <Badge key={t.id} variant="outline" className="py-1 px-3">
                        {t.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Client Dialog */}
      <Dialog open={showClientDialog} onOpenChange={(open) => { setShowClientDialog(open); if (!open) setEditingClient(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit B2B Client (Agency)' : 'Add B2B Client (Agency)'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Agency Code *</Label>
                <Input 
                  value={clientForm.agency_code} 
                  onChange={e => setClientForm({...clientForm, agency_code: e.target.value.toUpperCase()})} 
                  placeholder="e.g. AGY001"
                  disabled={!!editingClient}
                />
              </div>
              <div>
                <Label>Agency Name *</Label>
                <Input value={clientForm.agency_name} onChange={e => setClientForm({...clientForm, agency_name: e.target.value})} placeholder="Agency name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name *</Label>
                <Input value={clientForm.contact_name} onChange={e => setClientForm({...clientForm, contact_name: e.target.value})} placeholder="Contact person" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={clientForm.email} onChange={e => setClientForm({...clientForm, email: e.target.value})} placeholder="email@agency.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input value={clientForm.phone} onChange={e => setClientForm({...clientForm, phone: e.target.value})} placeholder="Phone number" />
              </div>
              <div>
                <Label>Phone 2</Label>
                <Input value={clientForm.phone_2 || ''} onChange={e => setClientForm({...clientForm, phone_2: e.target.value})} placeholder="Alternate phone number" />
              </div>
            </div>
            <div>
              <Label>Billing Type</Label>
              <Select value={clientForm.billing_type} onValueChange={v => setClientForm({...clientForm, billing_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="location_based">Location Based</SelectItem>
                  <SelectItem value="flat_rate">Flat Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea value={clientForm.address} onChange={e => setClientForm({...clientForm, address: e.target.value})} placeholder="Full address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Number</Label>
                <Input value={clientForm.gst_number} onChange={e => setClientForm({...clientForm, gst_number: e.target.value})} placeholder="GST Number" />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input value={clientForm.pan_number} onChange={e => setClientForm({...clientForm, pan_number: e.target.value})} placeholder="PAN Number" />
              </div>
            </div>
            
            {/* Location Based Pricing Configuration */}
            {clientForm.billing_type === 'location_based' && (
              <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    Pricing Configuration
                  </Label>
                </div>
                <p className="text-sm text-slate-500">Configure rates for each check type by state and city/location</p>
                
                {/* Add Pricing Form */}
                <div className="grid grid-cols-5 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Check Type *</Label>
                    <Select value={clientPricingForm.check_type} onValueChange={v => setClientPricingForm({...clientPricingForm, check_type: v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="address_pv">Address PV</SelectItem>
                        <SelectItem value="employment_pv">Employment PV</SelectItem>
                        <SelectItem value="education_pv">Education PV</SelectItem>
                        <SelectItem value="court_check_pv">Court Check</SelectItem>
                        <SelectItem value="id_check">ID Check</SelectItem>
                        <SelectItem value="credit_check">Credit Check</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Select value={clientPricingForm.state} onValueChange={v => setClientPricingForm({...clientPricingForm, state: v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">All States</SelectItem>
                        {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">City/Location</Label>
                    <Input 
                      className="h-9"
                      value={clientPricingForm.city} 
                      onChange={e => setClientPricingForm({...clientPricingForm, city: e.target.value})}
                      placeholder="e.g. Bangalore, ROK"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Price (₹) *</Label>
                    <Input 
                      type="number" 
                      className="h-9"
                      value={clientPricingForm.price} 
                      onChange={e => setClientPricingForm({...clientPricingForm, price: e.target.value})}
                      placeholder="500"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">TAT (Days)</Label>
                    <div className="flex gap-1">
                      <Input 
                        type="number" 
                        className="h-9 w-14"
                        value={clientPricingForm.tat_days} 
                        onChange={e => setClientPricingForm({...clientPricingForm, tat_days: parseInt(e.target.value) || 7})}
                      />
                      <Button size="sm" className="h-9" onClick={handleAddClientPricing}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Pricing List */}
                {clientForm.pricing && clientForm.pricing.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Check Type</TableHead>
                        <TableHead className="text-xs">State</TableHead>
                        <TableHead className="text-xs">City/Location</TableHead>
                        <TableHead className="text-xs">Price</TableHead>
                        <TableHead className="text-xs">TAT</TableHead>
                        <TableHead className="w-8"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientForm.pricing.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs">{p.check_type?.replace('_pv', '').replace('_', ' ').toUpperCase()}</TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-xs">{p.state === 'default' ? 'All' : p.state}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{p.city || '-'}</TableCell>
                          <TableCell className="text-xs">₹{p.price}</TableCell>
                          <TableCell className="text-xs">{p.tat_days} days</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-600" onClick={() => handleRemoveClientPricing(idx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                
                {(!clientForm.pricing || clientForm.pricing.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-2">No pricing configured yet. Add pricing above.</p>
                )}
              </div>
            )}
            
            {/* Additional Contacts for CC in Reports */}
            <div className="border rounded-lg p-4 bg-blue-50 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Additional Contacts (CC in Reports)
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={handleAddAdditionalContact}
                  className="h-8"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Contact
                </Button>
              </div>
              <p className="text-sm text-slate-500">Add email addresses to be CC'd when sending reports for this client</p>
              
              {clientForm.additional_contacts && clientForm.additional_contacts.length > 0 ? (
                <div className="space-y-2">
                  {clientForm.additional_contacts.map((contact, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 items-center bg-white p-2 rounded border">
                      <Input
                        placeholder="Name"
                        value={contact.name || ''}
                        onChange={e => handleUpdateAdditionalContact(idx, 'name', e.target.value)}
                        className="h-8"
                      />
                      <Input
                        type="email"
                        placeholder="Email *"
                        value={contact.email || ''}
                        onChange={e => handleUpdateAdditionalContact(idx, 'email', e.target.value)}
                        className="h-8"
                      />
                      <Input
                        placeholder="Role (e.g. HR Manager)"
                        value={contact.role || ''}
                        onChange={e => handleUpdateAdditionalContact(idx, 'role', e.target.value)}
                        className="h-8"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAdditionalContact(idx)}
                        className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-2">No additional contacts. Click "Add Contact" to add CC recipients.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowClientDialog(false); setEditingClient(null); }}>Cancel</Button>
            <Button onClick={editingClient ? handleUpdateClient : handleCreateClient}>
              {editingClient ? 'Update Client' : 'Create Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Initiate & Assign Dialog */}
      <Dialog open={showBulkInitiateDialog} onOpenChange={(open) => {
        setShowBulkInitiateDialog(open);
        if (!open) {
          setBulkInitiateVendorId('');
          setBulkInitiateAutoAssign(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Send className="w-5 h-5" />
              Bulk Initiate & Assign Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800">
                You are about to process <span className="font-bold">{vendorTeamSelectedChecks.length}</span> selected check(s).
              </p>
            </div>

            {/* Auto-Assign Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Auto-Assign Mode</p>
                <p className="text-xs text-gray-500">Auto-assign to vendors based on location matching</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${bulkInitiateAutoAssign ? 'text-green-600' : 'text-gray-400'}`}>
                  {bulkInitiateAutoAssign ? 'ON' : 'OFF'}
                </span>
                <Checkbox 
                  checked={bulkInitiateAutoAssign}
                  onCheckedChange={setBulkInitiateAutoAssign}
                  className="data-[state=checked]:bg-green-600"
                />
              </div>
            </div>

            {/* Auto-assign info */}
            {bulkInitiateAutoAssign && (
              <div className="p-2 bg-green-50 rounded-lg border border-green-200 text-xs text-green-700">
                <p className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Checks will be auto-assigned to vendors based on location/pincode matching.
                </p>
              </div>
            )}

            {/* Vendor Selection (Manual Mode) */}
            {!bulkInitiateAutoAssign && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Select Vendor to Assign Checks
                </p>
                <Select value={bulkInitiateVendorId} onValueChange={setBulkInitiateVendorId}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Choose a vendor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.filter(v => v.onboarding_status === 'active').map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        <div className="flex items-center gap-2">
                          <span>{v.name}</span>
                          {v.city && <span className="text-xs text-gray-500">({v.city})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-600">
                  All selected checks will be assigned to this vendor for verification.
                </p>
              </div>
            )}

            {/* Email Notification Info */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Mail className="w-4 h-4 text-amber-600" />
              <p className="text-xs text-amber-700">
                Verification initiation email will be sent to the assigned vendor automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkInitiateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkInitiate}
              disabled={bulkInitiateLoading || (!bulkInitiateAutoAssign && !bulkInitiateVendorId)}
              className="bg-green-600 hover:bg-green-700"
            >
              {bulkInitiateLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Initiate ({vendorTeamSelectedChecks.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise Insuff Dialog */}
      <Dialog open={showRaiseInsuffDialog} onOpenChange={(open) => {
        setShowRaiseInsuffDialog(open);
        if (!open) {
          setSelectedCheckForAction(null);
          setInsuffReason('');
          setInsuffRemarks('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <FileWarning className="w-5 h-5" />
              Raise Insufficiency
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCheckForAction && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Case:</strong> {selectedCheckForAction.internal_case_id}</p>
                <p><strong>Employee:</strong> {selectedCheckForAction.candidate_name}</p>
                <p><strong>Check Type:</strong> {selectedCheckForAction.check_type}</p>
              </div>
            )}
            
            <div>
              <Label className="font-medium">Select Reason *</Label>
              <Select value={insuffReason} onValueChange={setInsuffReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select insufficiency reason..." />
                </SelectTrigger>
                <SelectContent>
                  {insuffReasons.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-medium">Additional Remarks</Label>
              <Textarea 
                value={insuffRemarks}
                onChange={e => setInsuffRemarks(e.target.value)}
                placeholder="Enter any additional details or remarks..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRaiseInsuffDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleRaiseInsuff}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!insuffReason}
            >
              <FileWarning className="w-4 h-4 mr-2" />
              Raise Insuff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Fields Dialog */}
      <Dialog open={showEditFieldsDialog} onOpenChange={(open) => {
        setShowEditFieldsDialog(open);
        if (!open) {
          setSelectedCheckForAction(null);
          setEditFieldsData({});
          setEditFieldsRemarks('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Case Fields
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCheckForAction && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p><strong>Case:</strong> {selectedCheckForAction.internal_case_id}</p>
                <p><strong>Check Type:</strong> {selectedCheckForAction.check_type}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee Name</Label>
                <Input 
                  value={editFieldsData.name || ''}
                  onChange={e => setEditFieldsData({...editFieldsData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input 
                  value={editFieldsData.contact_number || ''}
                  onChange={e => setEditFieldsData({...editFieldsData, contact_number: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Textarea 
                value={editFieldsData.address || ''}
                onChange={e => setEditFieldsData({...editFieldsData, address: e.target.value})}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input 
                  value={editFieldsData.city || ''}
                  onChange={e => setEditFieldsData({...editFieldsData, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={editFieldsData.state || ''}
                  onChange={e => setEditFieldsData({...editFieldsData, state: e.target.value})}
                />
              </div>
              <div>
                <Label>Pincode</Label>
                <Input 
                  value={editFieldsData.pincode || ''}
                  onChange={e => setEditFieldsData({...editFieldsData, pincode: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Alternate Contact</Label>
              <Input 
                value={editFieldsData.alternate_contact || ''}
                onChange={e => setEditFieldsData({...editFieldsData, alternate_contact: e.target.value})}
              />
            </div>

            <div className="border-t pt-4">
              <Label className="font-medium text-orange-600">Reason for Edit / Remarks *</Label>
              <Textarea 
                value={editFieldsRemarks}
                onChange={e => setEditFieldsRemarks(e.target.value)}
                placeholder="Please provide reason for updating fields (required for re-initiation)..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFieldsDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleEditFields}
              disabled={!editFieldsRemarks}
            >
              <Edit className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Report Dialog */}
      <Dialog open={showBulkReportDialog} onOpenChange={setShowBulkReportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <FileText className="w-5 h-5" />
              Send Bulk Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                You are about to send reports for <span className="font-bold">{vendorTeamSelectedChecks.length}</span> selected check(s).
              </p>
            </div>

            {/* Preview of selected checks */}
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Client Code</TableHead>
                    <TableHead className="text-xs">Employee</TableHead>
                    <TableHead className="text-xs">Check Type</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Vendor</TableHead>
                    <TableHead className="text-xs">TAT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredB2BChecks
                    .filter(c => vendorTeamSelectedChecks.includes(c.check_id || c.id))
                    .map((check, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs font-medium">{check.client_case_id || check.agency_code}</TableCell>
                        <TableCell className="text-xs">{check.candidate_name}</TableCell>
                        <TableCell className="text-xs">{check.check_type}</TableCell>
                        <TableCell>{getStatusBadge(check.status)}</TableCell>
                        <TableCell className="text-xs">{check.vendor_name || 'N/A'}</TableCell>
                        <TableCell className="text-xs">{check.days_elapsed}/{check.tat_days}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <Mail className="w-4 h-4 text-green-600" />
              <p className="text-xs text-green-700">
                Reports will be emailed to the respective client contacts and CC recipients.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkReportDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkReport}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Reports ({vendorTeamSelectedChecks.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Cases</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Select Client *</Label>
                <Select value={caseForm.b2b_client_id} onValueChange={v => {
                  const client = clients.find(c => c.id === v);
                  setCaseForm({...caseForm, b2b_client_id: v, agency_code: client?.agency_code});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select agency" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.agency_name} ({c.agency_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agency Code</Label>
                <Input value={caseForm.agency_code || ''} disabled className="bg-gray-50" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Paste Data (Tab-separated)</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs"
                  onClick={() => handleDownloadTemplate('bulk', 'Bulk_Import_Template.xlsx')}
                >
                  <Download className="w-3 h-3 mr-1" /> Download Template
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Format: Client Case ID | Name | Father Name | DOB | Address | City | State | Pincode | Company | Check Type | Sub Type | Extra Info | Remarks
              </p>
              <Textarea 
                value={bulkImportData} 
                onChange={e => setBulkImportData(e.target.value)} 
                placeholder="Paste tab-separated data from Excel here..."
                className="h-48 font-mono text-sm"
              />
            </div>
            <div className="text-sm text-gray-500">
              <strong>Preview:</strong> {bulkImportData.trim().split('\n').filter(l => l.trim()).length} rows detected
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={!caseForm.b2b_client_id || !bulkImportData.trim()}>
              Import Cases
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {selectedCases.length} Cases</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Assignment Type</Label>
              <Select value={assignForm.assigned_type} onValueChange={v => setAssignForm({...assignForm, assigned_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="agent">Internal Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select {assignForm.assigned_type === 'vendor' ? 'Vendor' : 'Agent'}</Label>
              <Select value={assignForm.assigned_to} onValueChange={v => setAssignForm({...assignForm, assigned_to: v})}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.contact_person}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignCases}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Mapping Dialog */}
      <Dialog open={showVendorMappingDialog} onOpenChange={setShowVendorMappingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Vendor Location Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Location Name *</Label>
              <Input value={mappingForm.location_name} onChange={e => setMappingForm({...mappingForm, location_name: e.target.value})} placeholder="e.g. Mumbai, Delhi, default" />
            </div>
            <div>
              <Label>Check Type</Label>
              <Select value={mappingForm.check_type} onValueChange={v => setMappingForm({...mappingForm, check_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Check Types</SelectItem>
                  {checkTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.short}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign to Vendor *</Label>
              <Select value={mappingForm.vendor_id} onValueChange={v => setMappingForm({...mappingForm, vendor_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.contact_person}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorMappingDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveMapping}>Save Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Pricing Dialog */}
      <Dialog open={showVendorPricingDialog} onOpenChange={setShowVendorPricingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Vendor Pricing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor *</Label>
              <Select value={pricingForm.vendor_id} onValueChange={v => setPricingForm({...pricingForm, vendor_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.contact_person}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check Type *</Label>
                <Select value={pricingForm.check_type} onValueChange={v => setPricingForm({...pricingForm, check_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {checkTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.short}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub Check Type</Label>
                <Select value={pricingForm.sub_check_type} onValueChange={v => setPricingForm({...pricingForm, sub_check_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subCheckTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Location Name *</Label>
              <Input value={pricingForm.location_name} onChange={e => setPricingForm({...pricingForm, location_name: e.target.value})} placeholder="e.g. Mumbai, Delhi, default" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹) *</Label>
                <Input type="number" value={pricingForm.price} onChange={e => setPricingForm({...pricingForm, price: parseFloat(e.target.value)})} />
              </div>
              <div>
                <Label>TAT (Days)</Label>
                <Input type="number" value={pricingForm.tat_days} onChange={e => setPricingForm({...pricingForm, tat_days: parseInt(e.target.value)})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorPricingDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePricing}>Save Pricing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Invoice / Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={invoiceForm.b2b_client_id} onValueChange={v => setInvoiceForm({...invoiceForm, b2b_client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select agency" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.agency_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={invoiceForm.invoice_type} onValueChange={v => setInvoiceForm({...invoiceForm, invoice_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimate">Estimate</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period From</Label>
                <Input type="date" value={invoiceForm.period_from} onChange={e => setInvoiceForm({...invoiceForm, period_from: e.target.value})} />
              </div>
              <div>
                <Label>Period To</Label>
                <Input type="date" value={invoiceForm.period_to} onChange={e => setInvoiceForm({...invoiceForm, period_to: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>GST Rate (%)</Label>
              <Input type="number" value={invoiceForm.gst_rate} onChange={e => setInvoiceForm({...invoiceForm, gst_rate: parseFloat(e.target.value)})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateInvoice}>Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Document Scanning Dialog */}
      <Dialog open={showScanDialog} onOpenChange={(open) => {
        setShowScanDialog(open);
        if (!open) {
          setScanFile(null);
          setScannedRecords([]);
          setScanSource('');
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-purple-500" /> 
              AI-Powered Document Scanner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Step 1: Select Client */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Select Client *</Label>
                <Select value={caseForm.b2b_client_id || ''} onValueChange={v => {
                  const client = clients.find(c => c.id === v);
                  setCaseForm({...caseForm, b2b_client_id: v, agency_code: client?.agency_code});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select agency" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.agency_name} ({c.agency_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agency Code</Label>
                <Input value={caseForm.agency_code || ''} disabled className="bg-gray-100" />
              </div>
            </div>

            {/* Step 2: Upload Document */}
            <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  <FileImage className="w-8 h-8 text-blue-500" />
                  <FileSpreadsheet className="w-8 h-8 text-green-500" />
                  <FileText className="w-8 h-8 text-red-500" />
                </div>
                <div className="flex-1">
                  <Label className="font-semibold">Upload Document</Label>
                  <p className="text-sm text-slate-500">Supports: Images (JPG, PNG), Excel (.xlsx, .xls, .csv), PDF</p>
                  <Input 
                    type="file" 
                    accept=".jpg,.jpeg,.png,.pdf,.xlsx,.xls,.csv"
                    onChange={(e) => setScanFile(e.target.files?.[0])}
                    className="mt-2"
                  />
                </div>
                <Button 
                  onClick={handleScanDocument} 
                  disabled={!scanFile || scanning || !caseForm.b2b_client_id}
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                >
                  {scanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" /> Scan Document
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Step 3: Review Extracted Data */}
            {scannedRecords.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Extracted Records ({scannedRecords.length})
                    {scanSource && <Badge variant="outline">{scanSource === 'ai_scan' ? 'AI Extracted' : 'Excel Parsed'}</Badge>}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setScannedRecords([])}
                  >
                    Clear All
                  </Button>
                </div>
                
                <ScrollArea className="h-[300px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Father Name</TableHead>
                        <TableHead>DOB</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Sub Type</TableHead>
                        <TableHead>Company/Inst</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scannedRecords.map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Input 
                              value={record.name || ''} 
                              onChange={(e) => handleEditScannedRecord(idx, 'name', e.target.value)}
                              className="h-8 w-32"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              value={record.father_name || ''} 
                              onChange={(e) => handleEditScannedRecord(idx, 'father_name', e.target.value)}
                              className="h-8 w-28"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              value={record.dob || ''} 
                              onChange={(e) => handleEditScannedRecord(idx, 'dob', e.target.value)}
                              className="h-8 w-24"
                              placeholder="DD/MM/YYYY"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              value={record.city || ''} 
                              onChange={(e) => handleEditScannedRecord(idx, 'city', e.target.value)}
                              className="h-8 w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={record.check_type || 'address_pv'} 
                              onValueChange={(v) => handleEditScannedRecord(idx, 'check_type', v)}
                            >
                              <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {checkTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.short}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={record.sub_check_type || 'present'} 
                              onValueChange={(v) => handleEditScannedRecord(idx, 'sub_check_type', v)}
                            >
                              <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {subCheckTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-slate-600">
                              {record.company_name || record.university_name || record.college_name || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteScannedRecord(idx)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            {/* Info box when no records */}
            {scannedRecords.length === 0 && !scanning && (
              <div className="p-8 text-center text-slate-500 bg-slate-50 rounded-lg">
                <Scan className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Upload an Excel file, PDF, or image to extract verification data</p>
                <p className="text-sm mt-1">AI will automatically identify and extract candidate information</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowScanDialog(false);
              setScanFile(null);
              setScannedRecords([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleImportScannedRecords}
              disabled={scannedRecords.length === 0 || !caseForm.b2b_client_id}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Import {scannedRecords.length} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog open={!!showInvoiceViewDialog} onOpenChange={(open) => !open && setShowInvoiceViewDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showInvoiceViewDialog?.invoice_type === 'estimate' ? (
                <FileCheck2 className="w-5 h-5 text-blue-600" />
              ) : (
                <FileText className="w-5 h-5 text-green-600" />
              )}
              {showInvoiceViewDialog?.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} Details - {showInvoiceViewDialog?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {showInvoiceViewDialog && (
            <div className="space-y-4">
              {/* Type Banner */}
              {showInvoiceViewDialog.invoice_type === 'estimate' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                  <FileCheck2 className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-700 text-sm">
                    This is an <strong>Estimate</strong>. Send it to the client for approval before converting to invoice.
                  </span>
                </div>
              )}

              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Agency</p>
                  <p className="font-semibold">{showInvoiceViewDialog.agency_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <Badge 
                    variant="outline" 
                    className={showInvoiceViewDialog.invoice_type === 'estimate' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-green-300 text-green-700 bg-green-50'}
                  >
                    {showInvoiceViewDialog.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Period</p>
                  <p>{showInvoiceViewDialog.period_from || 'N/A'} - {showInvoiceViewDialog.period_to || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(showInvoiceViewDialog.status)}
                </div>
              </div>

              {/* Line Items */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead>#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showInvoiceViewDialog.items || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.check_type?.replace('_pv', ' PV').toUpperCase()}</p>
                            <p className="text-sm text-gray-500">{item.name}</p>
                          </div>
                        </TableCell>
                        <TableCell>{item.location_name || '-'}</TableCell>
                        <TableCell className="text-right">₹{(item.price || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>₹{(showInvoiceViewDialog.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>IGST ({showInvoiceViewDialog.gst_rate || 18}%)</span>
                  <span>₹{(showInvoiceViewDialog.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span className="text-green-600">₹{(showInvoiceViewDialog.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Notes */}
              {showInvoiceViewDialog.notes && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p>{showInvoiceViewDialog.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button variant="outline" size="sm" onClick={() => handleDownloadExcel(showInvoiceViewDialog)}>
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => generateInvoicePDF(showInvoiceViewDialog)}>
                <Printer className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowInvoiceViewDialog(null)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  openEmailDialog(showInvoiceViewDialog);
                  setShowInvoiceViewDialog(null);
                }}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
              {showInvoiceViewDialog?.invoice_type === 'estimate' && showInvoiceViewDialog?.status !== 'converted' && (
                <Button 
                  onClick={() => handleConvertToInvoice(showInvoiceViewDialog)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Convert to Invoice
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Send Dialog */}
      <Dialog open={!!showEmailDialog} onOpenChange={(open) => !open && setShowEmailDialog(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Send {showEmailDialog?.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} via Email
            </DialogTitle>
          </DialogHeader>
          
          {showEmailDialog && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className="bg-slate-50 p-3 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-semibold">{showEmailDialog.invoice_number}</p>
                  <p className="text-sm text-gray-500">{showEmailDialog.agency_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">₹{showEmailDialog.total?.toLocaleString()}</p>
                  <Badge 
                    variant="outline" 
                    className={showEmailDialog.invoice_type === 'estimate' ? 'border-blue-300 text-blue-700' : 'border-green-300 text-green-700'}
                  >
                    {showEmailDialog.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}
                  </Badge>
                </div>
              </div>

              {/* Email Form */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="recipient_email">Recipient Email *</Label>
                  <Input
                    id="recipient_email"
                    type="email"
                    value={emailForm.recipient_email}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                    placeholder="client@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cc_emails">CC (comma-separated)</Label>
                  <Input
                    id="cc_emails"
                    value={emailForm.cc_emails}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, cc_emails: e.target.value }))}
                    placeholder="accounts@example.com, manager@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email_subject">Subject (optional - auto-generated if empty)</Label>
                  <Input
                    id="email_subject"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder={`${showEmailDialog.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'} ${showEmailDialog.invoice_number} from JustVerified`}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email_message">Custom Message (optional)</Label>
                  <Textarea
                    id="email_message"
                    value={emailForm.message}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Add a custom message to include in the email..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="include_excel"
                    checked={emailForm.include_excel}
                    onCheckedChange={(checked) => setEmailForm(prev => ({ ...prev, include_excel: checked }))}
                  />
                  <Label htmlFor="include_excel" className="text-sm cursor-pointer">
                    Include Excel attachment with detailed data
                  </Label>
                </div>
              </div>

              {/* Attachments Preview */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-sm font-medium mb-2">Attachments:</p>
                <div className="flex gap-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Printer className="w-3 h-3" />
                    {showEmailDialog.invoice_number}.pdf
                  </Badge>
                  {emailForm.include_excel && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <FileSpreadsheet className="w-3 h-3" />
                      {showEmailDialog.invoice_number}.xlsx
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={!emailForm.recipient_email || sendingEmail}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send {showEmailDialog?.invoice_type === 'estimate' ? 'Estimate' : 'Invoice'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Recording Dialog */}
      <Dialog open={!!showPaymentDialog} onOpenChange={(open) => !open && setShowPaymentDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Record Payment - {showPaymentDialog?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {showPaymentDialog && (
            <div className="space-y-4">
              {/* Invoice Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Invoice Total</p>
                  <p className="text-xl font-bold">₹{showPaymentDialog.total?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Paid Amount</p>
                  <p className="text-xl font-bold text-emerald-600">₹{(showPaymentDialog.total_paid || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Balance Due</p>
                  <p className="text-xl font-bold text-red-600">
                    ₹{(showPaymentDialog.balance_due ?? showPaymentDialog.total)?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Payment History */}
              {invoicePayments.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-2 font-semibold flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Payment History ({invoicePayments.length})
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoicePayments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.payment_date}</TableCell>
                          <TableCell className="font-semibold text-emerald-600">₹{payment.amount?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {payment.payment_method?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{payment.reference_number || '-'}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* New Payment Form */}
              {(showPaymentDialog.payment_status !== 'paid') && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Banknote className="w-4 h-4" />
                    Record New Payment
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_amount">Amount *</Label>
                      <Input
                        id="payment_amount"
                        type="number"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_date">Payment Date *</Label>
                      <Input
                        id="payment_date"
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_method">Payment Method</Label>
                      <Select 
                        value={paymentForm.payment_method} 
                        onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reference_number">Reference Number</Label>
                      <Input
                        id="reference_number"
                        value={paymentForm.reference_number}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                        placeholder="Transaction ID / Cheque No."
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="payment_notes">Notes</Label>
                      <Input
                        id="payment_notes"
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Optional payment notes"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(null)}>
              Close
            </Button>
            {showPaymentDialog?.payment_status !== 'paid' && (
              <Button 
                onClick={handleRecordPayment}
                disabled={!paymentForm.amount || recordingPayment}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
              >
                {recordingPayment ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Record Payment
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outstanding Report Dialog */}
      <Dialog open={showOutstandingReport} onOpenChange={setShowOutstandingReport}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-red-600" />
              Outstanding Payment Report
            </DialogTitle>
          </DialogHeader>
          
          {outstandingData && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-red-600">Total Outstanding</p>
                    <p className="text-2xl font-bold text-red-800">
                      ₹{outstandingData.summary?.total_outstanding_amount?.toLocaleString() || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-amber-600">Clients with Dues</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {outstandingData.summary?.total_clients_with_dues || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-purple-600">Pending Invoices</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {outstandingData.summary?.total_invoices_outstanding || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* By Client Breakdown */}
              {outstandingData.by_client?.length > 0 ? (
                <div className="space-y-4">
                  {outstandingData.by_client.filter(c => c.total_outstanding > 0).map((client) => (
                    <Card key={client.client_id} className="border-l-4 border-l-red-400">
                      <CardHeader className="py-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">{client.agency_name}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-100 text-red-800 border-red-300">
                              Outstanding: ₹{client.total_outstanding?.toLocaleString()}
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDownloadStatement(client.client_id)}
                              className="text-blue-600"
                            >
                              <FileSpreadsheet className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setShowOutstandingReport(false);
                                openReminderDialog(client);
                              }}
                              className="bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600"
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Send Reminder
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="py-0 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Invoice #</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Total</TableHead>
                              <TableHead>Paid</TableHead>
                              <TableHead>Balance</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {client.invoices?.map((inv) => (
                              <TableRow key={inv.invoice_id}>
                                <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                                <TableCell>{inv.invoice_date}</TableCell>
                                <TableCell>₹{inv.total?.toLocaleString()}</TableCell>
                                <TableCell className="text-emerald-600">₹{inv.paid?.toLocaleString()}</TableCell>
                                <TableCell className="text-red-600 font-semibold">₹{inv.balance?.toLocaleString()}</TableCell>
                                <TableCell>{getStatusBadge(inv.status)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
                  <p>No outstanding payments! All invoices are paid.</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutstandingReport(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Reminder Dialog */}
      <Dialog open={!!showReminderDialog} onOpenChange={(open) => !open && setShowReminderDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Send Payment Reminder
            </DialogTitle>
          </DialogHeader>
          
          {showReminderDialog && clientStatement && (
            <div className="space-y-4">
              {/* Client Info */}
              <div className="bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{clientStatement.client?.agency_name}</h3>
                    <p className="text-sm text-gray-600">{clientStatement.client?.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Outstanding</p>
                    <p className="text-2xl font-bold text-red-600">
                      ₹{clientStatement.summary?.total_outstanding?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">{clientStatement.summary?.total_invoices} invoice(s)</p>
                  </div>
                </div>
              </div>

              {/* Aging Summary */}
              <div className="grid grid-cols-5 gap-2">
                <div className="text-center p-2 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600">Current</p>
                  <p className="font-bold text-emerald-700">₹{clientStatement.aging?.current?.toLocaleString() || 0}</p>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-yellow-600">1-30 Days</p>
                  <p className="font-bold text-yellow-700">₹{clientStatement.aging?.days_1_30?.toLocaleString() || 0}</p>
                </div>
                <div className="text-center p-2 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600">31-60 Days</p>
                  <p className="font-bold text-orange-700">₹{clientStatement.aging?.days_31_60?.toLocaleString() || 0}</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-600">61-90 Days</p>
                  <p className="font-bold text-red-700">₹{clientStatement.aging?.days_61_90?.toLocaleString() || 0}</p>
                </div>
                <div className="text-center p-2 bg-red-100 rounded-lg">
                  <p className="text-xs text-red-700">Over 90 Days</p>
                  <p className="font-bold text-red-800">₹{clientStatement.aging?.days_over_90?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Invoice Table Preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 font-semibold flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" />
                  Statement Preview ({clientStatement.items?.length || 0} invoices)
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Age</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(clientStatement.items || []).slice(0, 10).map((item) => (
                      <TableRow key={item.invoice_id}>
                        <TableCell className="font-mono">{item.invoice_number}</TableCell>
                        <TableCell>{item.invoice_date}</TableCell>
                        <TableCell>₹{item.total?.toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-600">₹{item.paid?.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600 font-semibold">₹{item.balance?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={`${getAgingColor(item.days_overdue)} text-white`}>
                            {item.days_overdue} days
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(clientStatement.items?.length || 0) > 10 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 text-sm">
                          ... and {clientStatement.items.length - 10} more invoice(s)
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Email Form */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reminder_email">Recipient Email *</Label>
                    <Input
                      id="reminder_email"
                      type="email"
                      value={reminderForm.recipient_email}
                      onChange={(e) => setReminderForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                      placeholder="client@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reminder_cc">CC (comma-separated)</Label>
                    <Input
                      id="reminder_cc"
                      value={reminderForm.cc_emails}
                      onChange={(e) => setReminderForm(prev => ({ ...prev, cc_emails: e.target.value }))}
                      placeholder="accounts@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reminder_message">Custom Message (optional)</Label>
                  <Textarea
                    id="reminder_message"
                    value={reminderForm.message}
                    onChange={(e) => setReminderForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Add a custom message to include in the reminder email..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Warning Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-800">Email will include:</p>
                  <ul className="list-disc list-inside text-amber-700 mt-1">
                    <li>Consolidated statement with all {clientStatement.items?.length || 0} outstanding invoice(s)</li>
                    <li>Aging analysis showing days overdue for each invoice</li>
                    <li>Total outstanding amount: ₹{clientStatement.summary?.total_outstanding?.toLocaleString()}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => handleDownloadStatement(showReminderDialog?.client_id)}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Download Statement
            </Button>
            <Button variant="outline" onClick={() => setShowReminderDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendReminder}
              disabled={!reminderForm.recipient_email || sendingReminder}
              className="bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600"
            >
              {sendingReminder ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Send Reminder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Reminder Preview Dialog */}
      <Dialog open={showAutoReminderPreview} onOpenChange={setShowAutoReminderPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-600" />
              Auto Reminder Preview
            </DialogTitle>
          </DialogHeader>
          
          {autoReminderPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-600">Clients Reviewed</p>
                    <p className="text-2xl font-bold">{autoReminderPreview.summary?.total_clients_reviewed || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-emerald-600">Will Send To</p>
                    <p className="text-2xl font-bold text-emerald-800">{autoReminderPreview.summary?.will_send_to || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-amber-600">Outstanding Amount</p>
                    <p className="text-2xl font-bold text-amber-800">
                      ₹{autoReminderPreview.summary?.total_outstanding_for_reminders?.toLocaleString() || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Settings Summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-semibold mb-1">Current Settings:</p>
                <p>
                  Intervals: {autoReminderPreview.settings?.reminder_intervals?.join(', ') || 'None'} days | 
                  Min Amount: ₹{autoReminderPreview.settings?.min_outstanding_amount || 0} | 
                  Frequency: Every {autoReminderPreview.settings?.reminder_frequency_days || 7} days
                </p>
              </div>

              {/* Client List */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Invoices</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Max Age</TableHead>
                      <TableHead>Last Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(autoReminderPreview.preview || []).map((item) => (
                      <TableRow key={item.client_id} className={item.will_send ? 'bg-emerald-50' : ''}>
                        <TableCell>
                          {item.will_send ? (
                            <Badge className="bg-emerald-100 text-emerald-800">✓ Will Send</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">{item.skip_reason || 'Skip'}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{item.agency_name}</TableCell>
                        <TableCell className="text-sm text-gray-500">{item.email || '-'}</TableCell>
                        <TableCell>{item.invoice_count}</TableCell>
                        <TableCell className="font-semibold text-red-600">₹{item.total_outstanding?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={`${getAgingColor(item.max_days_overdue)} text-white`}>
                            {item.max_days_overdue} days
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {item.days_since_last_reminder !== null ? `${item.days_since_last_reminder} days ago` : 'Never'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoReminderPreview(false)}>
              Close
            </Button>
            <Button 
              onClick={handleRunAutoReminders}
              disabled={runningAutoReminders || autoReminderPreview?.summary?.will_send_to === 0}
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
            >
              {runningAutoReminders ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send {autoReminderPreview?.summary?.will_send_to || 0} Reminder(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Case Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Approve Case
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p>Are you sure you want to approve this case for <strong>{selectedCaseForApproval?.employee?.name}</strong>?</p>
            <p className="text-sm text-slate-600">Once approved, the case will be sent to QC for processing.</p>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes for the QC team..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApprovalDialog(false); setSelectedCaseForApproval(null); setApprovalNotes(''); }}>
              Cancel
            </Button>
            <Button onClick={handleApproveCase} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />Approve Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Case Rejection Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Reject & Send Back
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p>Rejecting this case will send it back to <strong>{selectedCaseForApproval?.employee?.name}</strong> for corrections.</p>
            <div>
              <Label>Reason for Rejection <span className="text-red-500">*</span></Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please specify what needs to be corrected..."
                rows={4}
                required
              />
            </div>
            <p className="text-sm text-slate-500">The employee will receive an email notification with your feedback.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectionDialog(false); setSelectedCaseForApproval(null); setRejectionReason(''); }}>
              Cancel
            </Button>
            <Button onClick={handleRejectCase} variant="destructive" disabled={!rejectionReason.trim()}>
              <X className="w-4 h-4 mr-2" />Reject & Send Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Case Dialog */}
      <Dialog open={showCaseDialog} onOpenChange={setShowCaseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New B2B Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Client <span className="text-red-500">*</span></Label>
                <Select 
                  value={caseForm.b2b_client_id || ''} 
                  onValueChange={v => {
                    const client = clients.find(c => c.id === v);
                    setCaseForm({...caseForm, b2b_client_id: v, agency_code: client?.agency_code || ''});
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select Client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.agency_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client Case ID</Label>
                <Input 
                  value={caseForm.client_case_id || ''} 
                  onChange={e => setCaseForm({...caseForm, client_case_id: e.target.value})}
                  placeholder="Client's reference ID"
                />
              </div>
              <div>
                <Label>Joining Company <span className="text-red-500">*</span></Label>
                <Input 
                  value={caseForm.joining_company_name || ''} 
                  onChange={e => setCaseForm({...caseForm, joining_company_name: e.target.value})}
                  placeholder="Company name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Candidate Name <span className="text-red-500">*</span></Label>
                <Input 
                  value={caseForm.name || ''} 
                  onChange={e => setCaseForm({...caseForm, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label>Father's Name</Label>
                <Input 
                  value={caseForm.father_name || ''} 
                  onChange={e => setCaseForm({...caseForm, father_name: e.target.value})}
                  placeholder="Father's name"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>DOB</Label>
                <Input 
                  type="date"
                  value={caseForm.dob || ''} 
                  onChange={e => setCaseForm({...caseForm, dob: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={caseForm.phone || ''} 
                  onChange={e => setCaseForm({...caseForm, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>Location <span className="text-red-500">*</span></Label>
                <Input 
                  value={caseForm.location_name || ''} 
                  onChange={e => setCaseForm({...caseForm, location_name: e.target.value})}
                  placeholder="City/State"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check Type <span className="text-red-500">*</span></Label>
                <Select 
                  value={caseForm.check_type || ''} 
                  onValueChange={v => setCaseForm({...caseForm, check_type: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Check Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="address_pv">Address PV</SelectItem>
                    <SelectItem value="employment_pv">Employment PV</SelectItem>
                    <SelectItem value="education_pv">Education PV</SelectItem>
                    <SelectItem value="company_check_pv">Company Check PV</SelectItem>
                    <SelectItem value="court_check_pv">Court Check PV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub Check Type</Label>
                <Select 
                  value={caseForm.sub_check_type || 'present'} 
                  onValueChange={v => setCaseForm({...caseForm, sub_check_type: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Sub Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="permanent">Permanent</SelectItem>
                    <SelectItem value="past">Past</SelectItem>
                    <SelectItem value="past_2">Past 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address Fields */}
            {caseForm.check_type === 'address_pv' && (
              <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium">Address Details</h4>
                <div>
                  <Label>Address</Label>
                  <Textarea 
                    value={caseForm.address || ''} 
                    onChange={e => setCaseForm({...caseForm, address: e.target.value})}
                    placeholder="Full address"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input 
                      value={caseForm.city || ''} 
                      onChange={e => setCaseForm({...caseForm, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={caseForm.state || ''} onValueChange={v => setCaseForm({...caseForm, state: v})}>
                      <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input 
                      value={caseForm.pincode || ''} 
                      onChange={e => setCaseForm({...caseForm, pincode: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Employment Fields */}
            {caseForm.check_type === 'employment_pv' && (
              <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium">Employment Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input 
                      value={caseForm.company_name || ''} 
                      onChange={e => setCaseForm({...caseForm, company_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Input 
                      value={caseForm.designation || ''} 
                      onChange={e => setCaseForm({...caseForm, designation: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Company Address</Label>
                  <Textarea 
                    value={caseForm.company_address || ''} 
                    onChange={e => setCaseForm({...caseForm, company_address: e.target.value})}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date of Joining</Label>
                    <Input 
                      type="date"
                      value={caseForm.date_of_joining || ''} 
                      onChange={e => setCaseForm({...caseForm, date_of_joining: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Date of Relieving</Label>
                    <Input 
                      type="date"
                      value={caseForm.date_of_relieving || ''} 
                      onChange={e => setCaseForm({...caseForm, date_of_relieving: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Education Fields */}
            {caseForm.check_type === 'education_pv' && (
              <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium">Education Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>University Name</Label>
                    <Input 
                      value={caseForm.university_name || ''} 
                      onChange={e => setCaseForm({...caseForm, university_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>College Name</Label>
                    <Input 
                      value={caseForm.college_name || ''} 
                      onChange={e => setCaseForm({...caseForm, college_name: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Institution Address</Label>
                  <Textarea 
                    value={caseForm.institution_address || ''} 
                    onChange={e => setCaseForm({...caseForm, institution_address: e.target.value})}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Course</Label>
                    <Input 
                      value={caseForm.course || ''} 
                      onChange={e => setCaseForm({...caseForm, course: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Year of Passing</Label>
                    <Input 
                      value={caseForm.year_of_passing || ''} 
                      onChange={e => setCaseForm({...caseForm, year_of_passing: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label>Remarks</Label>
              <Textarea 
                value={caseForm.remarks || ''} 
                onChange={e => setCaseForm({...caseForm, remarks: e.target.value})}
                placeholder="Any additional remarks..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCaseDialog(false); setCaseForm({}); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCase} 
              disabled={!caseForm.b2b_client_id || !caseForm.name || !caseForm.check_type || !caseForm.location_name || !caseForm.joining_company_name}
            >
              <Plus className="w-4 h-4 mr-2" />Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Case Details Dialog */}
      <Dialog open={!!selectedViewCase} onOpenChange={(open) => !open && setSelectedViewCase(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5" />
              Case Details - {selectedViewCase?.internal_case_id}
            </DialogTitle>
          </DialogHeader>
          {selectedViewCase && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={
                      selectedViewCase.status === 'completed' ? 'bg-green-100 text-green-800' :
                      selectedViewCase.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                      selectedViewCase.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-slate-100 text-slate-800'
                    }>
                      {selectedViewCase.status?.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">QC Status</p>
                    <Badge variant="outline">{selectedViewCase.qc_status || 'Pending'}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Price</p>
                  <p className="text-xl font-semibold">₹{selectedViewCase.price?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Candidate Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Name</span>
                      <span className="font-medium">{selectedViewCase.name}</span>
                    </div>
                    {selectedViewCase.father_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Father's Name</span>
                        <span>{selectedViewCase.father_name}</span>
                      </div>
                    )}
                    {selectedViewCase.dob && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">DOB</span>
                        <span>{selectedViewCase.dob}</span>
                      </div>
                    )}
                    {selectedViewCase.phone && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Phone</span>
                        <span>{selectedViewCase.phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Check Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Check Type</span>
                      <Badge variant="outline">{selectedViewCase.check_type?.replace('_pv', ' PV').toUpperCase()}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sub Type</span>
                      <span>{selectedViewCase.sub_check_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Location</span>
                      <span>{selectedViewCase.location_name || selectedViewCase.city}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Agency</span>
                      <span>{selectedViewCase.agency_name}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Address Details */}
              {(selectedViewCase.address || selectedViewCase.city) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Address Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{selectedViewCase.address}</p>
                    <p className="text-slate-500">
                      {[selectedViewCase.city, selectedViewCase.state, selectedViewCase.pincode].filter(Boolean).join(', ')}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Employment Details */}
              {selectedViewCase.company_name && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Employment Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Company</span>
                      <span className="font-medium">{selectedViewCase.company_name}</span>
                    </div>
                    {selectedViewCase.designation && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Designation</span>
                        <span>{selectedViewCase.designation}</span>
                      </div>
                    )}
                    {selectedViewCase.company_address && (
                      <div>
                        <span className="text-slate-500">Company Address</span>
                        <p className="text-slate-700">{selectedViewCase.company_address}</p>
                      </div>
                    )}
                    <div className="flex gap-8">
                      {selectedViewCase.date_of_joining && (
                        <div>
                          <span className="text-slate-500">DOJ: </span>
                          <span>{selectedViewCase.date_of_joining}</span>
                        </div>
                      )}
                      {selectedViewCase.date_of_relieving && (
                        <div>
                          <span className="text-slate-500">DOR: </span>
                          <span>{selectedViewCase.date_of_relieving}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Education Details */}
              {selectedViewCase.university_name && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Education Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">University</span>
                      <span className="font-medium">{selectedViewCase.university_name}</span>
                    </div>
                    {selectedViewCase.college_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">College</span>
                        <span>{selectedViewCase.college_name}</span>
                      </div>
                    )}
                    {selectedViewCase.course && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Course</span>
                        <span>{selectedViewCase.course}</span>
                      </div>
                    )}
                    {selectedViewCase.year_of_passing && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Year of Passing</span>
                        <span>{selectedViewCase.year_of_passing}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Assignment Info */}
              {selectedViewCase.assigned_to && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" /> Assignment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Assigned To</span>
                      <span className="font-medium">{selectedViewCase.assigned_to_name || selectedViewCase.assigned_to}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Assigned Type</span>
                      <Badge variant="outline">{selectedViewCase.assigned_type}</Badge>
                    </div>
                    {selectedViewCase.assigned_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Assigned On</span>
                        <span>{new Date(selectedViewCase.assigned_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Remarks */}
              {selectedViewCase.remarks && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Remarks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{selectedViewCase.remarks}</p>
                  </CardContent>
                </Card>
              )}

              {/* Verification Result */}
              {selectedViewCase.verification_result && (
                <Card className={
                  selectedViewCase.verification_result === 'clear' ? 'border-green-200 bg-green-50' :
                  selectedViewCase.verification_result === 'discrepancy' ? 'border-red-200 bg-red-50' :
                  'border-yellow-200 bg-yellow-50'
                }>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Verification Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge className={
                      selectedViewCase.verification_result === 'clear' ? 'bg-green-600' :
                      selectedViewCase.verification_result === 'discrepancy' ? 'bg-red-600' :
                      'bg-yellow-600'
                    }>
                      {selectedViewCase.verification_result?.toUpperCase()}
                    </Badge>
                    {selectedViewCase.verification_remarks && (
                      <p className="mt-2 text-slate-700">{selectedViewCase.verification_remarks}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Timestamps */}
              <div className="flex gap-4 text-sm text-slate-500 pt-2 border-t">
                <span>Created: {new Date(selectedViewCase.created_at).toLocaleString()}</span>
                {selectedViewCase.updated_at && (
                  <span>Updated: {new Date(selectedViewCase.updated_at).toLocaleString()}</span>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedViewCase(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
